import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import Sidebar from "@/components/Sidebar";
import { CourierRatingCard } from "@/components/CourierRatingCard";
import HeaderAuthControl from "@/components/HeaderAuthControl";
import HomeCard from "@/components/HomeCard";
import KiosqueStrip from "@/components/content/KiosqueStrip";
import { ActionPreviewCard, ActionListCard, ActionPreparedCard, ActionClarifyCard, ActionCartAddCard, ActionDeniedCard } from "@/components/PrivateActionCards";
import AyaPriveHero, { AyaPriveMiniMask } from "@/components/AyaPriveHero";
import "@/components/AyaPriveHero.css";
// AyaNotch import supprimé — le masque a migré dans le halo (LiveBanner).
import FavoritesNoveltiesStrip from "@/components/FavoritesNoveltiesStrip";
// v3.0.0-rc.7-phaseCD — Phase D.2 : Rich response renderer + Wallet quicklink.
import RichResponseRenderer from "@/components/RichResponseRenderer";
import FlagCi from "@/components/FlagCi";
import { useAuth } from "@/hooks/useAuth";
import useConversations from "@/hooks/useConversations";
import { normalizeForDisplay } from "@/lib/ttsNormalizerClient";
import { LiveSession } from "@/lib/liveVoiceClient";
import TTSPlayButton from "@/components/TTSPlayButton";
import RagMediaBlock from "@/components/RagMediaBlock";
import SophisticatedLoader from "@/components/SophisticatedLoader";
import { PersonalityCardBlock } from "@/components/PersonalityCard";
import InputBar from "@/components/InputBar";
import AdresseCard from "@/components/AdresseCard";
import CommerceCard from "@/components/CommerceCard";
import AddressNavigateCard from "@/components/AddressNavigateCard";
import AgentToolBlock from "@/components/AgentToolBlock";
import JusticeCard from "@/components/JusticeCard";
import HealthCard from "@/components/HealthCard";
import DivertissementCard from "@/components/DivertissementCard";
import EmploiCard from "@/components/EmploiCard";
import GenericDirectoryCard from "@/components/GenericDirectoryCard";
import DetailPromptCard from "@/components/DetailPromptCard";
import AddressCreateCard from "@/components/AddressCreateCard";
import PurchaseConfirmationModal from "@/components/PurchaseConfirmationModal";
import AddressCreationTemplate from "@/rx-engine/templates/address_creation";
import OrderTrackingLive from "@/components/OrderTrackingLive";
import CriteriaFormCard from "@/components/CriteriaFormCard";
import FamilyBasketShareCard from "@/components/FamilyBasketShareCard";
import DiasporaRequestsCard from "@/components/DiasporaRequestsCard";
import GpsNavCard from "@/components/GpsNavCard";
import { readCart as readLocalCart } from "@/lib/localCartStore";
import HorizontalCardList from "@/components/HorizontalCardList";
import ImmersiveCard from "@/components/ImmersiveCard";
import LiveSubTabs from "@/components/LiveSubTabs";
import FeaturedCards from "@/components/FeaturedCards";
import SuggestedQuestionsChips from "@/components/SuggestedQuestionsChips";
import LiveBanner from "@/components/LiveBanner";
import { getCategoryImage, getJusticeImage, getHealthImage, getDivImage, getFoodImage, getBeauteImage, filterForIndex, LIVE_CARD } from "@/data/categoryImages";
import { getFeaturedItems } from "@/data/categoryFeaturedData";
import { CHAT_CATEGORIES } from "@/data/categoriesConfig"; import SERVICE_CATEGORIES from "@/data/servicesConfig";
// v2.19.39-sec11 — Action bar moderne : icônes SVG monochromes (plus d'emojis colorés).
import { Copy as IconCopy, ThumbsUp as IconThumbsUp, ThumbsDown as IconThumbsDown, RefreshCw as IconRefresh, Share2 as IconShare, MoreHorizontal as IconMore } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const MODES = [
  { value: "casual", label: "Casual" },
  { value: "street", label: "Nouchi" },
  { value: "equilibre", label: "Équilibré" },
  { value: "precis", label: "Précis" },
  { value: "creatif", label: "Créatif" },
];

// [SPRINT K] Liste source extraite vers data/categoriesConfig.js (ajoute 'services').
const FALLBACK_CATEGORIES = CHAT_CATEGORIES;

// v2.17.0a — Style du bouton icône header (toggles thème + voix).
const headerIconBtnStyle = {
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.14)",
  color: "#F1F5F9",
  width: 36,
  height: 36,
  borderRadius: 10,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  padding: 0,
  transition: "background 150ms ease, border-color 150ms ease",
};

// v2.8.0 — CATEGORY_ICON map remplacée par /data/categoryImages.js + <ImmersiveCard>.

const VS = { IDLE: "idle", LISTENING: "listening", PROCESSING: "processing", SPEAKING: "speaking" };

function prettyLabel(slug) { return (slug || "").replace(/_/g, " "); }

const PROVIDER_DISPLAY = {
  openai: "OpenAI",
  mistral: "Mistral",
  gemini: "Gemini",
  anthropic: "Claude",
};
function prettyProvider(p) {
  return PROVIDER_DISPLAY[p] || (p ? p[0].toUpperCase() + p.slice(1) : "LLM");
}

const WEB_SEARCH_MODES = [
  { value: "auto", label: "🌐 Auto" },
  { value: "on", label: "🌐 On" },
  { value: "off", label: "🌐 Off" },
];
const WEB_SEARCH_KEY = "jsonfirst_civ_web_search_mode";

// Replace inline [1], [2]… citations with clickable markdown links pointing to the matching source URL.
function linkifyCitations(text, sources) {
  if (!text || !Array.isArray(sources) || sources.length === 0) return text;
  return text.replace(/\[(\d{1,2})\]/g, (m, n) => {
    const i = parseInt(n, 10) - 1;
    const src = sources[i];
    if (src && src.url) return `[\\[${n}\\]](${src.url})`;
    return m;
  });
}

function genUserId() {
  try {
    const k = "jsonfirst_civ_userid";
    const existing = localStorage.getItem(k);
    if (existing) return existing;
    const id = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : "u-" + Math.random().toString(36).slice(2, 12);
    localStorage.setItem(k, id);
    return id;
  } catch (_) {
    return "u-" + Math.random().toString(36).slice(2, 12);
  }
}

// v2.19.29a — Chips de démarrage : 4 suggestions visibles juste au-dessus de l'input
// tant que l'utilisateur n'a pas envoyé son premier message. Tap → envoi direct.
// v2.19.29b — Plus utilisé : on rend désormais directement les `suggestedQuestions`
// du greeting actif au-dessus de l'input (cf. plus bas, juste avant <InputBar/>).

export default function Chat() {
  // v2.11.0 — react-router useNavigate pour /event/:address_civ.
  const navigate = useNavigate();
  const conv = useConversations();
  const activeMessagesRef = useRef([]); // Phase D.2 fix — ref live de active.messages pour stubIdx fiable post-double-appendMessage (closure async safe).
  const { user: authUser } = useAuth(); // v2.19.29a — pour interpoler {prenom} dans le greeting
  const [userId, setUserId] = useState(() => genUserId());
  // 2026-07-08 (owner, sessions par compte) — l'identité applicative suit le COMPTE :
  // connexion/déconnexion → useAuth met à jour jsonfirst_civ_userid et émet cet
  // événement ; le chat adopte la nouvelle identité sans rechargement.
  useEffect(() => {
    const onUseridChanged = (e) => {
      const next = (e && e.detail) || localStorage.getItem("jsonfirst_civ_userid");
      if (next && next !== userId) setUserId(next);
    };
    window.addEventListener("aya:userid-changed", onUseridChanged);
    return () => window.removeEventListener("aya:userid-changed", onUseridChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // v3.0.0-rc.8j — Cart checkout replay dans le chat.
  // Écoute `aya:cart-checkout-success` (émis par CartDrawer) et injecte le
  // même flow visuel que le chat native purchase confirm : (1) message user
  // récapitulant l'intent, (2) message agent rich_response avec les cartes
  // `order_tracking_ref` (suivi live via OrderTrackingLive, identiques au flow natif).
  useEffect(() => {
    const onCartCheckoutSuccess = (e) => {
      const detail = (e && e.detail) || {};
      const orders = Array.isArray(detail.orders) ? detail.orders : [];
      if (orders.length === 0) return;
      const activeId = conv.active?.id;
      if (!activeId) return;
      const isRecovery = !!detail.recovery;

      const merchantsCount = orders.length;
      const totalFcfa = detail.total_fcfa || orders.reduce((s, o) => s + (o.amount_fcfa || 0), 0);
      const fmtFcfa = (n) => Number(n || 0).toLocaleString("fr-FR") + " FCFA";

      // 1. Message user — traces l'intent explicite.
      conv.appendMessage(activeId, {
        role: "user",
        content: isRecovery
          ? `🔎 Suivi rouvert — ${orders[0].merchant_name || "commande"} · ${fmtFcfa(totalFcfa)}`
          : `🛒 Commande depuis le panier — ${merchantsCount} boutique${merchantsCount > 1 ? "s" : ""} · ${fmtFcfa(totalFcfa)}`,
        ts: Date.now(),
        source: isRecovery ? "order_recovery" : "cart_checkout",
      });

      // 2. Récap textuel adapté (commandes + réservations le cas échéant).
      const anyBooking = orders.some((o) => (o.items || []).some((it) => it.kind === "booking"));
      const anyProduct = orders.some((o) => (o.items || []).some((it) => (it.kind || "product") === "product"));
      const headerEmoji = isRecovery ? "🔎" : (anyBooking && !anyProduct ? "📅" : "✅");
      const headerLabel = isRecovery
        ? `Suivi de commande rouvert`
        : anyBooking && !anyProduct
          ? "Réservation(s) confirmée(s)"
          : anyBooking
            ? "Commande(s) et réservation(s) confirmées"
            : "Commande(s) confirmée(s)";
      const linesRecap = orders.map((o, i) => {
        const isBooking = (o.items || []).some((it) => it.kind === "booking");
        const kindIcon = isBooking ? "📅" : "🛍️";
        const label = isBooking ? "Réservation" : "Commande";
        const pinLine = o.pin ? ` · Code de réception : **${o.pin}**` : "";
        return `${i + 1}. ${kindIcon} **${o.merchant_name}** · ${label} \`${o.order_id?.slice(0, 8) || "—"}\`\n   ${o.item_summary || "—"}\n   Montant : **${fmtFcfa(o.amount_fcfa)}**${pinLine}${o.dispatch_started ? "\n   🛵 Livreur en recherche…" : (isBooking ? "" : "\n   ⏳ Dispatch en attente")}`;
      }).join("\n\n");

      const errorsBlock = Array.isArray(detail.errors) && detail.errors.length > 0
        ? `\n\n⚠️ ${detail.errors.length} groupe(s) non traité(s) : ${detail.errors.map((er) => `${er.merchant_id}=${er.error}`).join(", ")}`
        : "";

      const balanceLine = Number.isFinite(Number(detail.new_balance_fcfa))
        ? `\n\n💼 Nouveau solde escrow : **${fmtFcfa(detail.new_balance_fcfa)}**`
        : "";

      const footerLine = isRecovery
        ? "\n\n👉 Suis la livraison ci-dessous en temps réel."
        : "\n\n👉 Le suivi live démarre ci-dessous. Garde ton code de réception (PIN) confidentiel — tu le donnes UNIQUEMENT au livreur à la remise.";

      const recapText = isRecovery
        ? `${headerEmoji} **${headerLabel}**\n\n${linesRecap}${errorsBlock}${footerLine}`
        : `${headerEmoji} **${headerLabel}** — Gouvernance JSONFIRST validée ✓\n\n${linesRecap}${errorsBlock}${balanceLine}${footerLine}`;

      // 3. Construction ui_blocks : uniquement les cartes de suivi (le récap
      // texte est déjà rendu par MessageBubble via `msg.content` en markdown).
      const ui_blocks = orders.map((o) => ({
        type: "order_tracking_ref",
        order_id: o.order_id,
        user_id: userId,
        pin: o.pin,
        merchant_name: o.merchant_name,
      }));

      conv.appendMessage(activeId, {
        role: "agent",
        content: recapText,
        streaming: false,
        ts: Date.now(),
        source: "cart_checkout",
        agent_meta: {
          intent: "purchase",
          response_type: "rich",
          ui_blocks,
          entry_id: detail.parent_entry_id || null,
          llm_model: "cart-pipeline",
        },
      });
    };
    window.addEventListener("aya:cart-checkout-success", onCartCheckoutSuccess);
    return () => window.removeEventListener("aya:cart-checkout-success", onCartCheckoutSuccess);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, conv.active?.id]);

  // v3.7.4 — Notifications livraison : poll /notifications/pending (10s + focus)
  // et injecte chaque étape (acceptée, livreur en route, livrée…) comme
  // message AYA proactif dans la conversation active.
  useEffect(() => {
    const activeId = conv.active?.id;
    if (!activeId) return;
    let stopped = false;
    const poll = async () => {
      try {
        const token = localStorage.getItem("civ_user_token");
        if (!token) return;
        const r = await fetch(`${API}/v3/notifications/pending`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const d = await r.json();
        if (stopped || !d.ok || !Array.isArray(d.notifications) || d.notifications.length === 0) return;
        d.notifications.forEach((n) => {
          conv.appendMessage(activeId, {
            role: "agent",
            content: n.text,
            streaming: false,
            ts: Date.now(),
            source: "delivery_notification",
            notif_kind: n.kind,
            delivery_meta: n.meta || null,
          });
        });
      } catch (_) { /* réseau silencieux */ }
    };
    const iv = setInterval(poll, 10000);
    const t0 = setTimeout(poll, 1500);
    const onFocus = () => poll();
    window.addEventListener("focus", onFocus);
    return () => { stopped = true; clearInterval(iv); clearTimeout(t0); window.removeEventListener("focus", onFocus); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv.active?.id]);
  // v3.0.0-rc.7-phaseB-llm-toolcall-v1 — Phase B.3.1 : state modale confirmation agent
  const [pendingPurchase, setPendingPurchase] = useState(null);
  // 2026-07-02 — Address Gate (§27.5) : carte address_creation + reprise auto de la commande.
  const [acGate, setAcGate] = useState(null); // { payload, originalMessage, status: initial|validating|success }
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  // v2.7.1 — Catégories Justice (mode thématique alternatif).
  const [justiceCategories, setJusticeCategories] = useState([]);
  // v2.10.0 — Catégories Santé (3ème mode thématique).
  const [healthCategories, setHealthCategories] = useState([]);
  // v2.16.0 — Catégories Divertissement (4ème mode thématique).
  const [divCategories, setDivCategories] = useState([]);
  // v3.21.0 — Food : 10 onglets (épicerie 24/24, fast-food, allocodrome…).
  const [foodCategories, setFoodCategories] = useState([]);
  // v3.22.0 — Bien-être & Beauté : 10 onglets (barber, ongles, cils…).
  const [beauteCategories, setBeauteCategories] = useState([]);
  // v2.16.2 — Catégories Emploi (5ème mode thématique).
  const [emploiCategories, setEmploiCategories] = useState([]);
  // v2.17.0 — Catégories Administration + Finance (6ème + 7ème modes).
  const [administrationCategories, setAdministrationCategories] = useState([]);
  const [financeCategories, setFinanceCategories] = useState([]);
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(false);
  // v2.19.20 — Phase du loader sophistiqué (listening → rag_fetching →
  // tools_calling → streaming → post_processing). Mis à jour par les events
  // SSE pour matérialiser les étapes réelles du pipeline.
  const [loadingPhase, setLoadingPhase] = useState("listening");
  // v1.20.2 — Ref vers InputBar (input state vit dans le composant enfant).
  const inputBarRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // v1.17.2 — Desktop sidebar collapse (independent of mobile drawer).
  // v2.12.1 — Default closed on laptop + persisted via localStorage (key `civ_ai_sidebar_open`).
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem("civ_ai_sidebar_open");
      // saved === "1" → ouvert, saved === "0" ou null → fermé (défaut).
      return saved !== "1";
    } catch (_) { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("civ_ai_sidebar_open", desktopCollapsed ? "0" : "1"); } catch (_) {}
  }, [desktopCollapsed]);
  // v1.18.0 — Toggle barre catégories (persisté localStorage).
  const [categoriesCollapsed, setCategoriesCollapsed] = useState(() => {
    try { return localStorage.getItem("civ_categories_collapsed") === "1"; }
    catch (_) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("civ_categories_collapsed", categoriesCollapsed ? "1" : "0"); } catch (_) {}
  }, [categoriesCollapsed]);
  const [continuousMode, setContinuousMode] = useState(false);
  // v1.11.0 — Gemini Live session state (CASUAL only).
  // liveMode default OFF (v2.19.1) — STT classique plus fiable en preview iframe.
  // v2.19.4b — Gemini Live WS frontend supprimé : on garde uniquement
  // SpeechRecognition fr-FR. `liveMode` est désormais figé à `false`.
  const [liveMode] = useState(false);
  // Purge le legacy localStorage au mount (ancien flag v2.19.0 hérité).
  useEffect(() => {
    try { localStorage.removeItem("jsonfirst_civ_live_mode"); } catch (_) {}
  }, []);
  useEffect(() => {
    try { localStorage.setItem("jsonfirst_civ_live_mode", liveMode ? "1" : "0"); } catch (_) {}
  }, [liveMode]);
  const [liveState, setLiveState] = useState("idle"); // idle | listening | speaking | interrupted | closed
  const [liveMuted, setLiveMuted] = useState(false);
  const [liveTranscriptUser, setLiveTranscriptUser] = useState("");
  const [liveTranscriptAgent, setLiveTranscriptAgent] = useState("");
  const [liveError, setLiveError] = useState(null);
  // v1.14.0 — Theme toggle (light/dark) persisted in localStorage.
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem("civ_ai_theme");
      if (saved === "light" || saved === "dark") return saved;
      // v1.17.0 — Dark by default (override prefers-color-scheme).
      return "dark";
    } catch { return "dark"; }
  });
  useEffect(() => {
    try {
      document.documentElement.setAttribute("data-theme", theme);
      localStorage.setItem("civ_ai_theme", theme);
    } catch (_) {}
  }, [theme]);
  const liveSessionRef = useRef(null);
  const [voiceOutputEnabled, setVoiceOutputEnabled] = useState(() => {
    try {
      const v = localStorage.getItem("jsonfirst_civ_voice_out");
      return v === null ? true : v === "1";
    } catch (_) { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("jsonfirst_civ_voice_out", voiceOutputEnabled ? "1" : "0"); } catch (_) {}
  }, [voiceOutputEnabled]);
  const [responseLang, setResponseLang] = useState(() => {
    try { return localStorage.getItem("jsonfirst_civ_response_lang") || "auto"; }
    catch (_) { return "auto"; }
  });
  useEffect(() => {
    try { localStorage.setItem("jsonfirst_civ_response_lang", responseLang); } catch (_) {}
  }, [responseLang]);
  const [newspapers, setNewspapers] = useState([]); // legacy alias of `partners` filtered to news
  const [partners, setPartners] = useState([]); // all active partners across all categories
  const [carouselCategory, setCarouselCategory] = useState(null); // null = closed; set to a category slug to open
  const [paymentTarget, setPaymentTarget] = useState(null); // {id, name, domain, ...}
  const [webSearchMode, setWebSearchMode] = useState(() => {
    try { return localStorage.getItem(WEB_SEARCH_KEY) || "auto"; }
    catch (_) { return "auto"; }
  });
  useEffect(() => {
    try { localStorage.setItem(WEB_SEARCH_KEY, webSearchMode); } catch (_) {}
  }, [webSearchMode]);

  // Voice session state
  const [voiceState, setVoiceState] = useState(VS.IDLE);
  const [interimTranscript, setInterimTranscript] = useState("");
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const lastFinalRef = useRef("");
  const continueLoopRef = useRef(false); // true while session active
  const noSpeechTimerRef = useRef(null);

  // Streaming + audio queue refs (for chunked TTS + interrupt)
  const abortCtrlRef = useRef(null);
  const audioQueueRef = useRef([]);
  const audioPlayingRef = useRef(false);
  const [streamingActive, setStreamingActive] = useState(false);
  const [audioActive, setAudioActive] = useState(false);
  // v2.19.21e — Doc attaché via la trombone (1 à la fois, TTL backend 15 min).
  const [attachedDoc, setAttachedDoc] = useState(null); // { doc_id, name, kind, preview, size }
  const [docUploading, setDocUploading] = useState(false);
  const interruptedRef = useRef(false);

  const scrollRef = useRef(null);

  const active = conv.active;
  useEffect(() => { activeMessagesRef.current = active?.messages || []; }, [active?.messages]); // Phase D.2 fix — sync ref live.
  // v1.17.0 — Patch persona mode of active conversation via Outils dropdown.
  // v2.7.0 — Wrapped : sync towards localStorage("civ_ai_llm_profile") for persistence.
  const setMode = useCallback((nextMode) => {
    if (active?.id) conv.updateMode(active.id, nextMode);
    try { localStorage.setItem("civ_ai_llm_profile", nextMode); } catch (_) {}
  }, [active?.id, conv]);
  // v1.17.0 — Rapide / Équilibré / Précis (UI-only state for now).
  const [rapideMode, setRapideMode] = useState("balanced");
  // v2.7.0 — Theme mode (Quotidien / Justice) persisté en localStorage.
  const [themeMode, setThemeMode] = useState(() => {
    // v3.21.0 — Default mode = "food" (request user 2026-08) + migration
    // one-shot des sessions existantes vers Food.
    try {
      if (!localStorage.getItem("civ_food_default_v1")) {
        localStorage.setItem("civ_food_default_v1", "1");
        localStorage.setItem("civ_ai_theme_mode", "food");
        return "food";
      }
      return localStorage.getItem("civ_ai_theme_mode") || "food";
    }
    catch (_) { return "food"; }
  });
  // v2.9.0 — Onglet actif sous la barre catégories. Drive le rendu des FeaturedCards.
  // Persisté en RAM uniquement (reset à chaque mount). Init: "live" (1ère catégorie).
  const [activeCategory, setActiveCategory] = useState(null);
  // v2.15.4 — Sous-onglets Live SUPPRIMÉS. Conserve un default au cas où getFeaturedItems("live") s'y réfère ailleurs.
  const [liveSubCat, setLiveSubCat] = useState("sport");
  // v2.9.0 — Cache des events live + compteurs par sous-catégorie.
  const [liveEvents, setLiveEvents] = useState([]);
  const [liveCounts, setLiveCounts] = useState({});
  useEffect(() => {
    try { localStorage.setItem("civ_ai_theme_mode", themeMode); } catch (_) {} if (themeMode === "services") { setCategories(SERVICE_CATEGORIES.map(c => c.slug)); } else if (categories[0] && SERVICE_CATEGORIES.some(s => s.slug === categories[0])) { setCategories(FALLBACK_CATEGORIES); }
    // v2.7.1 — Toast d'activation du mode Justice.
    if (themeMode === "justice" && justiceCategories.length > 0) {
      toast.success(`🎭 Mode Justice activé — ${justiceCategories.length} catégories disponibles`);
    }
    // v2.10.0 — Toast d'activation du mode Santé.
    if (themeMode === "sante" && healthCategories.length > 0) {
      toast.success(`🏥 Mode Santé activé — ${healthCategories.length} catégories disponibles`);
    }
    if (themeMode === "divertissement" && divCategories.length > 0) {
      toast.success(`🌴 Mode Détente activé — ${divCategories.length} catégories de loisirs`);
    }
    if (themeMode === "food" && foodCategories.length > 0) {
      toast.success(`🍽️ Mode Food activé — ${foodCategories.length} catégories gourmandes`);
    }
    if (themeMode === "beaute" && beauteCategories.length > 0) {
      toast.success(`💅 Mode Bien-être & Beauté activé — ${beauteCategories.length} univers`);
    }
    if (themeMode === "emploi" && emploiCategories.length > 0) {
      toast.success(`💼 Mode Emploi activé — ${emploiCategories.length} catégories métier`);
    }
    if (themeMode === "administration" && administrationCategories.length > 0) {
      toast.success(`🛂 Mode Administration activé — ${administrationCategories.length} catégories de démarches`);
    }
    if (themeMode === "finance" && financeCategories.length > 0) {
      toast.success(`💰 Mode Finance activé — ${financeCategories.length} catégories financières`);
    }
  }, [themeMode, justiceCategories.length, healthCategories.length, divCategories.length, foodCategories.length, beauteCategories.length, emploiCategories.length, administrationCategories.length, financeCategories.length]);
  const mode = active?.mode || "casual";

  // v2.19.1 — Greetings per theme mode (fetched once at mount).
  const [themeModesGreetings, setThemeModesGreetings] = useState({});
  // v2.19.2 — Track conv IDs that already received a greeting (shared between
  // applyThemeModeTransition and the auto-greeting useEffect to avoid dupes).
  const _greetingBootedSetRef = useRef(new Set());
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/theme-modes/greetings`).then((r) => {
      if (cancelled) return;
      const m = r?.data?.modes || {};
      setThemeModesGreetings(m);
    }).catch(() => { /* silent — chips fallback to empty */ });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // v2.19.1 — Confirm modal for mode-switch when current conv has user messages.
  const [pendingModeSwitch, setPendingModeSwitch] = useState(null); // { from, to }
  const _prevThemeModeRef = useRef(themeMode);
  const _themeModeMountedRef = useRef(false);

  // Function: applies the mode switch (creates a new conv).
  // v3.0.0-rc.8r — La bulle greeting séparée est SUPPRIMÉE — le tagline mode-spécifique
  // vit désormais à l'intérieur de la HomeCard. On garde uniquement la création
  // de la conversation fraîche + le marquage "greeted" pour éviter la ré-injection.
  const applyThemeModeTransition = useCallback((nextMode) => {
    const llmMode = (active && active.mode) || mode || "casual";
    const fresh = conv.create(llmMode);
    _greetingBootedSetRef.current.add(fresh.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv, active, mode]);

  // Detect real user-driven themeMode change → confirm if user has typed before.
  useEffect(() => {
    // Skip first effect run (mount restoration from localStorage).
    if (!_themeModeMountedRef.current) {
      _themeModeMountedRef.current = true;
      _prevThemeModeRef.current = themeMode;
      return;
    }
    const prev = _prevThemeModeRef.current;
    if (prev === themeMode) return;
    _prevThemeModeRef.current = themeMode;
    const hasUserMsg = (active?.messages || []).some((m) => m.role === "user");
    if (hasUserMsg) {
      setPendingModeSwitch({ from: prev, to: themeMode });
    } else {
      // Empty chat → apply directly (just inject greeting).
      applyThemeModeTransition(themeMode);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeMode]);

  // Inject greeting whenever the active conversation is empty AND no greeting
  // has been injected yet for THIS conversation id (Set defined earlier).
  // v3.0.0-rc.8m — SUPPRIMÉ : le "message d'accueil LLM" est remplacé par
  // le tagline mode-spécifique intégré à la HomeCard. On conserve les
  // suggested_questions comme chips (à ré-injecter séparément si besoin),
  // mais plus de bulle agent "Bienvenue sur AYA" à l'ouverture.
  useEffect(() => {
    if (!active?.id) return;
    if (_greetingBootedSetRef.current.has(active.id)) return;
    _greetingBootedSetRef.current.add(active.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);

  // v2.7.0 — Au premier mount avec conversation active, restaure le profil LLM persisté
  // s'il diffère du mode actuel de la conversation (cas du retour utilisateur).
  const _restoredLlmProfileRef = useRef(false);
  useEffect(() => {
    if (_restoredLlmProfileRef.current || !active?.id) return;
    try {
      const stored = localStorage.getItem("civ_ai_llm_profile");
      if (stored && stored !== mode) {
        const valid = ["casual","street","equilibre","precis","creatif","business"];
        if (valid.includes(stored)) {
          conv.updateMode(active.id, stored);
        }
      }
    } catch (_) {}
    _restoredLlmProfileRef.current = true;
  }, [active?.id, mode, conv]);

  // ---- Live voice (Gemini 2.0) session handlers — CASUAL only ----
  // v1.14.0 — Per-turn buffers persisted as chat bubbles when speaking → listening.
  const livePendingTurnRef = useRef({ user: "", agent: "", sponsored: null, disclosure: null });
  const livePrevStateRef = useRef("idle");

  const flushLiveTurnToBubbles = () => {
    const t = livePendingTurnRef.current;
    if (!t.user.trim() && !t.agent.trim()) {
      livePendingTurnRef.current = { user: "", agent: "", sponsored: null, disclosure: null };
      return;
    }
    const convId = active?.id;
    if (!convId) return;
    if (t.user.trim()) {
      conv.appendMessage(convId, { role: "user", text: t.user.trim(), voice: true, ts: Date.now() });
    }
    if (t.agent.trim()) {
      conv.appendMessage(convId, {
        role: "agent",
        text: t.agent.trim(),
        voice: true,
        ts: Date.now(),
        sponsored_cards: t.sponsored || undefined,
        sponsored_disclosure: t.disclosure || undefined,
      });
    }
    livePendingTurnRef.current = { user: "", agent: "", sponsored: null, disclosure: null };
  };

  const startLiveSession = async () => {
    if (liveSessionRef.current) return;
    if (mode !== "casual") { toast.error("La voix live est dispo uniquement en mode casual."); return; }
    setLiveError(null);
    setLiveTranscriptUser("");
    setLiveTranscriptAgent("");
    livePendingTurnRef.current = { user: "", agent: "", sponsored: null, disclosure: null };
    livePrevStateRef.current = "idle";
    const s = new LiveSession({
      backendUrl: process.env.REACT_APP_BACKEND_URL,
      onState: (st) => {
        const prev = livePrevStateRef.current;
        // End-of-turn detection: speaking → listening means the agent finished.
        if (prev === "speaking" && (st === "listening" || st === "idle")) {
          flushLiveTurnToBubbles();
          setLiveTranscriptUser("");
          setLiveTranscriptAgent("");
        }
        livePrevStateRef.current = st;
        setLiveState(st);
      },
      onUserTranscript: (t) => {
        livePendingTurnRef.current.user += t;
        setLiveTranscriptUser((prev) => prev + t);
      },
      onAgentTranscript: (t) => {
        livePendingTurnRef.current.agent += t;
        setLiveTranscriptAgent((prev) => prev + t);
      },
      onSponsoredCards: (msg) => {
        // Attach to the upcoming agent bubble (current turn).
        livePendingTurnRef.current.sponsored = Array.isArray(msg.cards) ? msg.cards : null;
        livePendingTurnRef.current.disclosure = msg.disclosure || null;
      },
      onError: (e) => {
        setLiveError(e.message || e.code || "live_error");
        toast.error(e.message || e.code || "Erreur voix live");
      },
      onClose: () => {
        // On any close, flush whatever was pending so the user sees the turn
        // even if they hung up mid-sentence.
        flushLiveTurnToBubbles();
        setLiveState("closed");
        liveSessionRef.current = null;
      },
    });
    try {
      await s.start({ userId, mode: "casual" });
      liveSessionRef.current = s;
    } catch (e) {
      setLiveError(String(e.message || e));
      toast.error("Micro inaccessible ou WS bloqué : " + (e.message || e));
    }
  };
  const stopLiveSession = () => {
    if (liveSessionRef.current) { try { liveSessionRef.current.stop(); } catch (_) {} }
    liveSessionRef.current = null;
    setLiveState("closed");
  };
  const toggleLiveMute = () => {
    if (!liveSessionRef.current) return;
    const m = liveSessionRef.current.toggleMute();
    setLiveMuted(m);
  };
  // Close the live session if the persona switches away from casual (live is
  // casual-only in this phase).
  useEffect(() => {
    if (mode !== "casual" && liveSessionRef.current) stopLiveSession();
  }, [mode]);
  // Cleanup on unmount.
  useEffect(() => () => stopLiveSession(), []);

  // ---- Bootstrap data ----
  useEffect(() => {
    axios.get(`${API}/categories`).then((r) => {
      if (Array.isArray(r.data?.categories) && r.data.categories.length) {
        setCategories(r.data.categories);
      }
    }).catch(() => {});
    // v2.7.1 — Fetch Justice categories (pour le mode thématique justice).
    axios.get(`${API}/justice/categories`).then((r) => {
      if (Array.isArray(r.data?.categories)) setJusticeCategories(r.data.categories);
    }).catch(() => {});
    // v2.10.0 — Fetch Health categories (pour le mode thématique santé).
    axios.get(`${API}/health/categories`).then((r) => {
      if (Array.isArray(r.data?.categories)) setHealthCategories(r.data.categories);
    }).catch(() => {});
    // v2.16.0 — Fetch Divertissement categories (4ème mode thématique).
    axios.get(`${API}/v3/divertissement/categories`).then((r) => {
      if (Array.isArray(r.data?.categories)) setDivCategories(r.data.categories);
    }).catch(() => {});
    // v3.21.0 — Fetch Food categories (10 onglets, mode par défaut).
    axios.get(`${API}/v3/food/categories`).then((r) => {
      if (Array.isArray(r.data?.categories)) setFoodCategories(r.data.categories);
    }).catch(() => {});
    // v3.22.0 — Fetch Bien-être & Beauté categories.
    axios.get(`${API}/v3/beaute/categories`).then((r) => {
      if (Array.isArray(r.data?.categories)) setBeauteCategories(r.data.categories);
    }).catch(() => {});
    // v2.16.2 — Fetch Emploi categories (5ème mode thématique).
    axios.get(`${API}/v3/emploi/categories`).then((r) => {
      if (Array.isArray(r.data?.categories)) setEmploiCategories(r.data.categories);
    }).catch(() => {});
    // v2.17.0 — Fetch Administration + Finance categories.
    axios.get(`${API}/v3/administration/categories`).then((r) => {
      if (Array.isArray(r.data?.categories)) setAdministrationCategories(r.data.categories);
    }).catch(() => {});
    axios.get(`${API}/v3/finance/categories`).then((r) => {
      if (Array.isArray(r.data?.categories)) setFinanceCategories(r.data.categories);
    }).catch(() => {});
    axios.get(`${API}/manifest`).then((r) => {
      if (r.data && r.data.protocol) setManifest(r.data);
    }).catch(() => {});
    axios.get(`${API}/newspapers`).then((r) => {
      if (Array.isArray(r.data?.newspapers)) setNewspapers(r.data.newspapers);
    }).catch(() => {});
    axios.get(`${API}/partners`).then((r) => {
      if (Array.isArray(r.data?.partners)) setPartners(r.data.partners);
    }).catch(() => {});
    // v2.9.0 — Bootstrap : on récupère uniquement les compteurs par sous-catégorie.
    // Le fetch des events visibles est piloté par le useEffect dépendant de
    // [activeCategory, liveSubCat] pour éviter la race condition (l'appel sans
    // filtre écrasait parfois l'appel filtré).
    axios.get(`${API}/live/events`).then((r) => {
      const counts = r.data?._meta?.total_per_sub_category || {};
      setLiveCounts(counts);
    }).catch(() => {});
  }, []);

  // v2.9.0 — Quand le sous-onglet Live change, fetch les events filtrés.
  useEffect(() => {
    if (activeCategory !== "live") return;
    axios.get(`${API}/live/events`, { params: { sub_category: liveSubCat } })
      .then((r) => { if (Array.isArray(r.data?.events)) setLiveEvents(r.data.events); })
      .catch(() => {});
  }, [activeCategory, liveSubCat]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [active?.messages, loading, interimTranscript, voiceState]);

  // ---- Audio queue (chunked TTS) ----
  const playNextIfIdle = () => {
    if (audioPlayingRef.current) return;
    const next = audioQueueRef.current.shift();
    if (!next) {
      // queue drained
      if (!streamingActive) setAudioActive(false);
      return;
    }
    audioPlayingRef.current = true;
    setAudioActive(true);
    const a = new Audio(next.src);
    audioRef.current = a;
    a.onended = () => {
      audioPlayingRef.current = false;
      audioRef.current = null;
      playNextIfIdle();
    };
    a.onerror = () => {
      audioPlayingRef.current = false;
      audioRef.current = null;
      playNextIfIdle();
    };
    const p = a.play();
    if (p && typeof p.catch === "function") {
      p.catch((err) => {
        console.warn("[audio] play failed:", err.message);
        audioPlayingRef.current = false;
        audioRef.current = null;
        playNextIfIdle();
      });
    }
  };

  const enqueueAudioChunk = ({ seq, audio_base64, mimeType }) => {
    if (!audio_base64) return;
    audioQueueRef.current.push({
      seq: seq || 0,
      src: `data:${mimeType || "audio/mpeg"};base64,${audio_base64}`,
    });
    audioQueueRef.current.sort((a, b) => a.seq - b.seq);
    playNextIfIdle();
  };

  const stopAllAudio = () => {
    audioQueueRef.current = [];
    audioPlayingRef.current = false;
    if (audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.src = ""; } catch (_) {}
      audioRef.current = null;
    }
    setAudioActive(false);
  };

  // ---- Interrupt handler ----
  const interrupt = () => {
    interruptedRef.current = true;
    // Abort SSE fetch (server detects via res.on('close'))
    if (abortCtrlRef.current) {
      try { abortCtrlRef.current.abort(); } catch (_) {}
      abortCtrlRef.current = null;
    }
    stopAllAudio();
    setStreamingActive(false);
    setLoading(false);
    // Mark current agent message as interrupted (best-effort: last agent msg)
    if (active) {
      const idx = active.messages.length - 1;
      if (idx >= 0 && active.messages[idx]?.role === "agent" && !active.messages[idx]?.isGreeting) {
        conv.updateMessage(active.id, idx, (m) => ({ ...m, streaming: false, interrupted: true }));
      }
    }
    setVoiceState(VS.IDLE);
    continueLoopRef.current = false; // do not auto-restart mic after manual interrupt
  };

  // ---- Message action handlers (copy, like, dislike, regenerate, share, more) ----
  const handleMessageAction = useCallback(async (action, message, index) => {
    if (!active) return;
    const conversationId = active.id;

    switch (action) {
      case "copy": {
        try {
          await navigator.clipboard.writeText(message.content || "");
          toast.success("Copié ✓");
        } catch (e) {
          toast.error("Copie impossible");
        }
        break;
      }
      case "like":
      case "dislike": {
        const isLike = action === "like";
        const next = isLike
          ? { liked: !message.liked, disliked: false }
          : { disliked: !message.disliked, liked: false };
        conv.updateMessage(conversationId, index, (m) => ({ ...m, ...next }));
        // Best-effort POST to /api/feedback
        const rating = (isLike ? !message.liked : !message.disliked) ? (isLike ? 1 : -1) : 0;
        if (rating !== 0) {
          fetch(`${API}/feedback`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              messageId: `${conversationId}:${index}`,
              conversationId,
              mode,
              rating,
              content: (message.content || "").slice(0, 500),
            }),
          }).catch(() => {});
        }
        break;
      }
      case "regenerate": {
        // Find the user message immediately preceding this agent message
        const prev = active.messages[index - 1];
        if (!prev || prev.role !== "user") {
          toast.error("Message utilisateur introuvable");
          break;
        }
        // Reset the current agent bubble & bump counter, then re-stream INTO it.
        const prevRegenCount = message.regenCount || 0;
        conv.updateMessage(conversationId, index, () => ({
          role: "agent",
          content: "",
          ts: Date.now(),
          streaming: true,
          regenCount: prevRegenCount + 1,
          regenerating: true,
        }));
        await sendMessageLive(prev.content, {
          source: "regenerate",
          replaceIndex: index,
          regenCount: prevRegenCount + 1,
        });
        break;
      }
      case "share": {
        const shareText = `${message.content}\n\nVia AYA 🇨🇮`;
        try {
          if (navigator.share) {
            await navigator.share({ title: "AYA", text: shareText });
          } else {
            const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
            window.open(url, "_blank", "noopener");
          }
        } catch (_) { /* user cancelled */ }
        break;
      }
      case "more": {
        const choice = window.prompt("Action ? json | delete | report", "json");
        if (!choice) break;
        if (choice === "delete") {
          if (window.confirm("Supprimer ce message ?")) {
            conv.removeMessage(conversationId, index);
          }
        } else if (choice === "json") {
          try {
            await navigator.clipboard.writeText(JSON.stringify(message, null, 2));
            toast.success("JSON copié");
          } catch (_) { toast.error("Copie impossible"); }
        } else if (choice === "report") {
          fetch(`${API}/feedback`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              messageId: `${conversationId}:${index}`,
              conversationId, mode, rating: -1,
              content: (message.content || "").slice(0, 500),
            }),
          }).catch(() => {});
          toast.success("Signalé. Merci.");
        }
        break;
      }
      default: break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, mode]);

  // v2.0.0 — Trigger géolocalisation + génération adresse via /api/v2.
  // Appelé soit après réponse non-stream (data.requires_geolocation) soit
  // après event SSE 'meta' avec requires_geolocation:true.
  const runAddressFlow = useCallback(async (conversationId, agentIndex) => {
    if (!navigator.geolocation) {
      conv.updateMessage(conversationId, agentIndex, (m) => ({
        ...m, content: (m.content || "") + "\n\n⚠️ Géolocalisation non supportée par le navigateur.", streaming: false,
      }));
      return;
    }
    try {
      // 1. Fetch communes (cache simple si déjà fait).
      const cr = await fetch(`${API}/v2/addressing/communes`).then((r) => r.json());
      const communes = cr.communes || [];
      // 2. Demande position.
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
      });
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      // 3. Find nearest street via Haversine.
      const hav = (a, b, c, d) => {
        const R = 6371000;
        const dLat = ((c - a) * Math.PI) / 180;
        const dLon = ((d - b) * Math.PI) / 180;
        const x = Math.sin(dLat / 2) ** 2 + Math.cos((a * Math.PI) / 180) * Math.cos((c * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
        return Math.round(R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x)));
      };
      let best = null; let bestD = Infinity;
      for (const cm of communes) {
        for (const s of cm.streets || []) {
          const d = hav(lat, lng, s.start_lat, s.start_lon);
          if (d < bestD) { bestD = d; best = { ...s, commune: cm.name }; }
        }
      }
      if (!best) {
        conv.updateMessage(conversationId, agentIndex, (m) => ({
          ...m, content: (m.content || "") + "\n\n⚠️ Aucune rue connue à proximité (Yopougon, Cocody, Abobo, Marcory, Plateau).", streaming: false,
        }));
        return;
      }
      // 4. POST generate.
      const r2 = await fetch(`${API}/v2/addressing/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          latitude: lat, longitude: lng,
          commune: best.commune, street_id: best.street_id, street_name: best.nom,
          start_lat: best.start_lat, start_lon: best.start_lon,
        }),
      }).then((r) => r.json());
      if (r2?.ok) {
        conv.updateMessage(conversationId, agentIndex, (m) => ({
          ...m,
          streaming: false,
          adresse_card: r2.address,
        }));
      } else {
        conv.updateMessage(conversationId, agentIndex, (m) => ({
          ...m, content: (m.content || "") + `\n\n⚠️ Erreur génération : ${r2?.error || "?"}`, streaming: false,
        }));
      }
    } catch (e) {
      conv.updateMessage(conversationId, agentIndex, (m) => ({
        ...m, content: (m.content || "") + `\n\n⚠️ Géolocalisation refusée ou indisponible : ${e.message || e.code || "?"}`, streaming: false,
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // v3.0.0-rc.7-phaseB-B33-ux-integration-fix-v1 — Détection regex purchase intent (Bug A)
  //   Préfère faux positifs > faux négatifs : modale s'ouvre, user clique Annuler si erreur.
  //   Couvre commande/achat/paiement/réservation + expressions familières CIV (fais venir, livre-moi).
  const PURCHASE_INTENT_RE = /\b(command[ez]?(?:r|-moi|moi)?|command(?:er|é|ée|és|ées)|achat|achète|achete[rz]?|acheter|paie[rz]?|paye[rz]?|payer|réserv(?:e|er|ez|é)|reserv(?:e|er|ez)|veux\s+acheter|fais\s+venir|livre[rz]?[-\s]?moi)\b/i;
  // 2026-07-02 — MARKETPLACE PRODUIT (vision owner) : recherche de BIENS → cartes
  // produits multi-vendeurs via l'orchestrateur v3 (les services restent au flux LLM).
  const PRODUCT_SEARCH_RE = /\b(montre|cherche|trouve|veux|voudrais|propose|donne|command\w*|ach[eè]te|besoin)\b[\s\S]{0,80}\b(jean(?:s)?|chaussures?|baskets?|sneakers?|robes?|pagnes?|t[ée]l[ée](?:vision)?|tv|t[ée]l[ée]phone|smartphone|samsung|iphone|tecno|frigo|r[ée]frig[ée]rateur|cong[ée]lateur|machine\s+[àa]\s+laver|lave[-\s]linge|micro[-\s]?ondes|ventilateur|climatiseur|clim|fer\s+[àa]\s+repasser|blender|mixeur|cuisini[èe]re|riz|huile|lait|sucre|farine|spaghettis?|p[âa]tes|tomate|eau\s+min[ée]rale|caf[ée]|nescaf[ée]|courses|savon|lessive|omo|dentifrice|couches|pampers|javel|marmites?|casseroles?|verres?|shawarma|pizza|garba|attiek[ée]|parac[ée]tamol|doliprane|m[ée]dicaments?|vitamines?|fleurs?|bouquets?|roses|orchid[ée]e|coussins?|miroirs?|lampes?|d[ée]co(?:ration)?|appartements?|studios?|villas?|voitures?|v[ée]hicules?|motos?)\b/i;
  const PRODUCT_VERB_RE = /\b(montre|cherche|trouve|veux|voudrais|propose|donne|command\w*|ach[eè]te|besoin)\b/i;
  // 2026-07-03 — Mots-clés DYNAMIQUES du catalogue vivant (produits ajoutés par
  // les commerçants via l'éditeur B2B) : rafraîchis au montage, fusionnés au routage.
  const dynamicKwRef = useRef(new Set());
  useEffect(() => {
    fetch(`${API}/v3/marketplace/keywords`)
      .then((r) => r.json())
      .then((d) => { if (d.ok && Array.isArray(d.keywords)) dynamicKwRef.current = new Set(d.keywords); })
      .catch(() => {});
  }, []);
  const _matchesDynamicProduct = (text) => {
    if (!PRODUCT_VERB_RE.test(text)) return false;
    const toks = text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").split(/[^a-z0-9]+/);
    return toks.some((t) => t.length >= 4 && dynamicKwRef.current.has(t));
  };

  // v3.0.0-rc.7-phaseB-B33-ux-integration-fix-v1 — Routeur orchestrator v3 (LLM_TOOLCALL_V1)
  //   Refactorisé depuis le dispatcher `/agent ` opt-in B.3.1, désormais réutilisable.
  //   Appelle POST /api/v3/agent/chat (Claude Haiku tool-calling + JSONFIRST guard + intent detection).
  //   Si intent==='purchase' → setPendingPurchase(modal_payload) → PurchaseConfirmationModal s'ouvre.
  // v3.22.0 — Filtre "Ma Commune" : géoloc rapide → commune la plus proche
  // (cache localStorage) ; les adresses de la commune passent en tête + badge.
  const COMMUNE_CENTROIDS = [["ABB",5.4326,-4.0428],["ADJ",5.3565,-4.0227],["ATT",5.3391,-4.0508],["COC",5.3599,-3.9673],["KOU",5.2907,-3.9459],["MAR",5.3005,-3.9814],["PLT",5.3266,-4.0173],["PBT",5.2564,-3.9268],["TRE",5.3081,-4.0125],["YOP",5.3457,-4.0893],["BIN",5.3550,-3.8850],["ANY",5.4946,-4.0517],["SON",5.3167,-4.2500]];
  const getUserCommuneCode = async () => {
    try {
      const cached = localStorage.getItem("civ_user_commune_code");
      if (cached) return cached;
      const pos = await new Promise((res, rej) => {
        if (!navigator.geolocation) return rej(new Error("no_geo"));
        navigator.geolocation.getCurrentPosition(res, rej, { timeout: 2500, maximumAge: 600000 });
      });
      const { latitude: la, longitude: lo } = pos.coords;
      let best = null; let bd = Infinity;
      for (const [code, clat, clng] of COMMUNE_CENTROIDS) {
        const dd = (clat - la) ** 2 + (clng - lo) ** 2;
        if (dd < bd) { bd = dd; best = code; }
      }
      if (best) localStorage.setItem("civ_user_commune_code", best);
      return best;
    } catch (_) { return null; }
  };
  const tagNearYou = (items, cc) => {
    if (!cc) return items;
    const near = []; const far = [];
    for (const it of items) {
      if (it.commune_code === cc) near.push({ ...it, near_you: true });
      else far.push(it);
    }
    return [...near, ...far];
  };

  // v3.22.0 — Food chat : "je veux un garba" → adresses Food direct (zéro LLM).
  const FOOD_DISH_MAP = [
    [/\balloco/i, "allocodrome"],
    [/garba|foutou|placali|kedjenou|sauce\s+graine|atti[ée]k[ée]|akp[eè]ssi|gombo|riz\s+sauce/i, "traditionnel"],
    [/burger|s?hawarma|chawarma|tacos|pizza|frites|nuggets|hamburger/i, "fastfood"],
    [/brais[ée]|choukouya|brochettes?|grillades?|dibi/i, "braise"],
    [/poisson|carpe|m[ée]rou|machoiron|\bsole\b/i, "lagune"],
    [/cr[êe]pes?|g[âa]teaux?|glaces?|beignets?|desserts?|cupcakes?|p[âa]tisserie|d[ée]gu[ée]/i, "gouter"],
    [/\bjus\b|bissap|gnamakoudji|gingembre|smoothie/i, "jus"],
    [/caf[ée]|petit[- ]d[ée]j|omelette|croissant/i, "petitdej"],
    [/[ée]picerie|courses de nuit|d[ée]pannage/i, "epicerie"],
  ];
  const FOOD_INTENT_RE = /\b(je veux|j'ai envie|je cherche|o[ùu] (?:manger|trouver|acheter)|donne[- ]?moi|trouve[- ]?moi|propose[- ]?moi|j'ai faim)\b/i;
  // v3.23.0 — Suggestions selon l'heure : petit-déj le matin, braisé le soir.
  const mealByHour = () => {
    const h = new Date().getHours();
    if (h < 11) return { cat: "petitdej", phrase: "c'est l'heure du petit-déj ☕" };
    if (h < 15) return { cat: "traditionnel", phrase: "un bon plat du jour s'impose 🍲" };
    if (h < 18) return { cat: "gouter", phrase: "l'heure parfaite pour une pause sucrée 🍰" };
    if (h < 23) return { cat: "braise", phrase: "c'est l'heure du braisé 🍗" };
    return { cat: "epicerie", phrase: "il est tard — les épiceries 24/24 te sauvent 🛒" };
  };
  const FOOD_IDEA_RE = /id[ée]es? (?:de )?repas|je mange quoi|quoi manger|on mange quoi|sugg[èe]re[- ]?moi|une suggestion/i;
  const detectFoodDish = (text) => {
    const t = String(text || "");
    // Les achats/commandes gardent le flux orchestrateur (panier, livraison).
    if (/command|ach[eè]t|livr|panier/i.test(t)) return null;
    const wantsIdea = FOOD_IDEA_RE.test(t);
    if (!FOOD_INTENT_RE.test(t) && !wantsIdea) return null;
    for (const [re, cat] of FOOD_DISH_MAP) {
      const m = t.match(re);
      if (m) return { cat, dish: m[0].toLowerCase() };
    }
    if (wantsIdea || /j'ai faim/i.test(t)) {
      const meal = mealByHour();
      return { cat: meal.cat, dish: null, phrase: meal.phrase };
    }
    return null;
  };
  const sendFoodDish = async (msgText, hit) => {
    if (!active?.id) return null;
    conv.appendMessage(active.id, { role: "user", content: msgText, ts: Date.now() });
    try {
      const r = await axios.get(`${API}/v3/food?category=${hit.cat}&limit=13`);
      let items = Array.isArray(r.data?.items) ? r.data.items : [];
      const cc = await getUserCommuneCode();
      items = tagNearYou(items, cc);
      const catLabel = items[0]?.category_label || hit.cat;
      const near = items.filter((i) => i.near_you).length;
      const prenom = authUser?.display_name?.split(" ")[0] || "";
      pushQuickSearch(active.id, {
        role: "agent", ts: Date.now(), kind: "system_quick_search", isLocal: true,
        content: `😋 ${hit.dish ? `${hit.dish.charAt(0).toUpperCase()}${hit.dish.slice(1)}, bonne idée${prenom ? ` ${prenom}` : ""} !` : `${hit.phrase ? `${prenom ? `${prenom}, ` : ""}${hit.phrase} !` : `On va te régaler${prenom ? ` ${prenom}` : ""} !`}`} Voici ${items.length} adresses « ${catLabel} »${near > 0 ? " — celles de ta commune d'abord 📍" : ""}. Clique 🚀 GPS pour y aller.`,
        rag_media: [
          { type: "div_grid", data: { category: { id: hit.cat, label: catLabel }, items } },
          { type: "detail_prompt", data: { prompt_id: `dp-${Date.now()}`, domain: "food", category_id: hit.cat, category_label: catLabel } },
        ],
      });
    } catch (_) { toast.error("Impossible de charger les adresses Food"); }
    return null;
  };


  // v3.24.0 — "mes réservations" → RDV beauté à venir + rappel jour J.
  const MY_BOOKINGS_RE = /mes\s+r[ée]servations?|mes\s+rendez[- ]?vous|mes\s+rdv/i;
  const sendMyBookings = async (msgText) => {
    if (!active) return null;
    conv.appendMessage(active.id, { role: "user", content: msgText, ts: Date.now() });
    const tk = localStorage.getItem("civ_user_token");
    if (!tk) {
      conv.appendMessage(active.id, { role: "agent", ts: Date.now(), content: "🔐 Connecte-toi pour voir tes réservations beauté 💅" });
      return null;
    }
    try {
      const r = await fetch(`${API}/v3/beaute/bookings/mine`, { headers: { Authorization: `Bearer ${tk}` } });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.error || "erreur");
      if (!d.bookings.length) {
        conv.appendMessage(active.id, { role: "agent", ts: Date.now(), content: "📅 Aucune réservation pour l'instant. Dis-moi **« trouve-moi un barber »** ou **« je veux faire mes ongles »** et réserve ton créneau en 2 clics 💅" });
        return null;
      }
      const lines = d.bookings.map((b) => `${b.is_today ? "🎉 **C'EST AUJOURD'HUI !** " : "📅 "}**${b.salon_name}** (${b.commune}) — ${b.day.toLowerCase()} à **${b.slot}** · code \`${String(b.booking_id).slice(-6).toUpperCase()}\`${b.status === "confirmed_pro" ? " · ✓ confirmé par le salon" : ""}`);
      const todayCount = d.bookings.filter((b) => b.is_today).length;
      conv.appendMessage(active.id, {
        role: "agent", ts: Date.now(),
        content: `💅 Tes **${d.bookings.length} réservation${d.bookings.length > 1 ? "s" : ""}**${todayCount > 0 ? ` — dont **${todayCount} aujourd'hui**, n'oublie pas ! ⏰` : ""} :\n\n${lines.join("\n")}`,
      });
    } catch (e) {
      conv.appendMessage(active.id, { role: "agent", ts: Date.now(), content: `❌ Impossible de charger tes réservations : ${e?.message || e}` });
    }
    return null;
  };

  // v3.23.0 — Beauté chat : "trouve-moi un barber" → salons direct + commune.
  const BEAUTE_MAP = [
    [/barber|coiffeur\s+(homme|pour homme)|d[ée]grad[ée]|coupe\s+homme|\bbarbe\b/i, "barber"],
    [/coiffeuse|coiffure(\s+femme)?|brushing|lissage|coloration/i, "coiffure_femme"],
    [/tresses?|nattes?|braids|perruques?|tissage/i, "tresses"],
    [/ongles?|manucure|p[ée]dicure|\bnails?\b/i, "ongles"],
    [/\bcils\b|sourcils|microblading|\blash/i, "cils"],
    [/soins?\s+(du\s+)?visage|hydrafacial|gommage\s+visage/i, "soins_visage"],
    [/massage|\bspa\b/i, "massage_spa"],
    [/maquillage|make[- ]?up|maquilleuse/i, "maquillage"],
    [/hammam|savon\s+noir/i, "hammam"],
    [/salle\s+de\s+sport|fitness|\bgym\b|yoga|muscu/i, "fitness"],
  ];
  const BEAUTE_INTENT_RE = /\b(trouve|cherche|je veux|j'ai besoin|donne[- ]?moi|propose[- ]?moi|o[ùu] (?:trouver|faire|me faire)|il me faut|r[ée]server?)\b/i;
  const detectBeaute = (text) => {
    const t = String(text || "");
    if (/command|ach[eè]t|livr|panier/i.test(t)) return null;
    if (!BEAUTE_INTENT_RE.test(t)) return null;
    for (const [re, cat] of BEAUTE_MAP) {
      const m = t.match(re);
      if (m) return { cat, term: m[0].toLowerCase() };
    }
    return null;
  };
  const sendBeauteSuggest = async (msgText, hit) => {
    if (!active?.id) return null;
    conv.appendMessage(active.id, { role: "user", content: msgText, ts: Date.now() });
    try {
      const r = await axios.get(`${API}/v3/beaute?category=${hit.cat}&limit=13`);
      let items = Array.isArray(r.data?.items) ? r.data.items : [];
      const cc = await getUserCommuneCode();
      items = tagNearYou(items, cc);
      const catLabel = items[0]?.category_label || hit.cat;
      const near = items.filter((i) => i.near_you).length;
      pushQuickSearch(active.id, {
        role: "agent", ts: Date.now(), kind: "system_quick_search", isLocal: true,
        content: `💅 Pas de souci ! Voici ${items.length} adresses « ${catLabel} »${near > 0 ? " — celles de ta commune d'abord 📍" : ""}. Tu peux **réserver un créneau** directement sur la carte 📅`,
        rag_media: [
          { type: "div_grid", data: { category: { id: hit.cat, label: catLabel }, items } },
          { type: "detail_prompt", data: { prompt_id: `dp-${Date.now()}`, domain: "beaute", category_id: hit.cat, category_label: catLabel } },
        ],
      });
    } catch (_) { toast.error("Impossible de charger les adresses Beauté"); }
    return null;
  };

  // v3.20.0 — GPS conversationnel : "conduis-moi à la cathédrale" → AYA sort
  // le GPS avec destination pré-remplie (zéro LLM). Les codes CIV-XXX gardent
  // leur flux backend existant (address_navigate).
  const GPS_NAV_RE = /(?:condui[st][\s-]*moi|emm[èe]ne[\s-]*moi|am[èe]ne[\s-]*moi|d[ée]pose[\s-]*moi|je veux aller|je dois aller|comment (?:aller|me rendre)|itin[ée]raire\s+(?:vers|pour|jusqu'[àa])|navigue\s+(?:vers|[àa])|sors[\s-]*moi le gps\s+(?:pour|vers))\s+(?:jusqu'[àa]\s+|vers\s+|chez\s+|[àa]\s+|au\s+|aux\s+|en\s+)?(.{2,90})/i;

  const sendGpsNav = (msgText, destQuery) => {
    if (!active) return null;
    conv.appendMessage(active.id, { role: "user", content: msgText, ts: Date.now() });
    const prenom = authUser?.display_name?.split(" ")[0] || authUser?.first_name || "";
    conv.appendMessage(active.id, {
      role: "agent", ts: Date.now(),
      content: `🧭 Oui${prenom ? ` ${prenom}` : ""}, je te sors le GPS prêt à démarrer ! Vérifie la destination ci-dessous puis confirme 🚀`,
      gps_nav: { query: destQuery },
    });
    return null;
  };

  // v3.18.0 — Espace Diaspora : "mes demandes" / "aide ma famille" → cartes
  // des demandes du moteur unique aya_requests directement dans le chat.
  const DIASPORA_REQ_RE = /(mes\s+demandes)|(aide[rz]?\s+(ma|la)\s+famille)|(m(on|a)\s+(tableau\s+de\s+bord|dashboard|espace)\s+diaspora)|(espace\s+diaspora)|(mes\s+paniers?\s+famille)/i;

  const sendDiasporaRequests = async (msgText) => {
    if (!active) return null;
    conv.appendMessage(active.id, { role: "user", content: msgText, ts: Date.now() });
    const drToken = localStorage.getItem("civ_user_token");
    if (!drToken) {
      conv.appendMessage(active.id, {
        role: "agent", ts: Date.now(),
        content: "🔐 Connecte-toi (icône profil en haut) pour voir tes demandes : paniers famille, factures, projets, solidarité… tout ton Espace Diaspora 🌍",
      });
      return null;
    }
    conv.appendMessage(active.id, { role: "agent", content: "⏳ Je rassemble tes demandes…", ts: Date.now() });
    await new Promise((r) => setTimeout(r, 60));
    const drIdx = Math.max(0, activeMessagesRef.current.length - 1);
    try {
      const r = await fetch(`${API}/v3/requests/mine?role=all&limit=6`, { headers: { Authorization: `Bearer ${drToken}` } });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.error || "chargement impossible");
      if (!d.requests.length) {
        conv.updateMessage(active.id, drIdx, (m) => ({
          ...m,
          content: "🌍 Tu n'as pas encore de demande en cours. Depuis ton **Espace Diaspora**, tu peux suivre paniers famille, factures, projets business, installation et actions solidaires — tout au même endroit.",
          diaspora_requests: { items: [] },
        }));
        return null;
      }
      const pending = d.requests.filter((x) => ["created", "accepted"].includes(x.status)).length;
      conv.updateMessage(active.id, drIdx, (m) => ({
        ...m,
        content: `🌍 Voici tes **${d.count} dernières demandes**${pending > 0 ? ` — dont **${pending} en attente** de prise en charge` : ""}. Tout est suivi dans ton Espace Diaspora 💛`,
        diaspora_requests: { items: d.requests },
      }));
    } catch (e) {
      conv.updateMessage(active.id, drIdx, (m) => ({ ...m, content: `❌ Impossible de charger tes demandes : ${e?.message || String(e)}` }));
    }
    return null;
  };

  // v3.17.1 — Panier Famille : "fais payer mon panier par mon frère" → AYA crée
  // le lien de paiement diaspora directement dans le chat (zéro appel LLM).
  const FAMILY_BASKET_RE = /(panier\s*famille)|(fai(?:s|re)\s+payer\s+(mon|le|ce)\s+panier)|((mon|ma|un[e]?)\s+(proche|fr[eè]re|s(?:œ|oe)ur|tonton|tantie|cousin[e]?|oncle|papa|maman|m[eè]re|p[eè]re|famille)[^.!?]{0,40}\b(paie|paye|payer|r[eè]gle)\b)|(\b(paie|paye|payer)\b[^.!?]{0,40}\b(depuis|de)\s+l['’]?[ée]tranger)/i;

  const sendFamilyBasket = async (msgText) => {
    if (!active) return null;
    conv.appendMessage(active.id, { role: "user", content: msgText, ts: Date.now() });
    const items = readLocalCart();
    if (!items.length) {
      conv.appendMessage(active.id, {
        role: "agent", ts: Date.now(),
        content: "💛 Le **Panier Famille** permet à un proche (même à l'étranger 🌍) de payer ton panier — AYA livre ensuite chez toi à Abidjan.\n\nTon panier est vide pour l'instant : ajoute d'abord des articles (bouton ➕ sur les produits), puis redis-moi **« panier famille »** et je génère le lien de paiement à partager sur WhatsApp.",
      });
      return null;
    }
    const fbToken = localStorage.getItem("civ_user_token");
    if (!fbToken) {
      conv.appendMessage(active.id, {
        role: "agent", ts: Date.now(),
        content: "🔐 Connecte-toi d'abord (icône profil en haut à droite) pour que je crée ton lien **Panier Famille** — ton proche saura ainsi que le panier vient bien de toi 💛",
      });
      return null;
    }
    conv.appendMessage(active.id, { role: "agent", content: "⏳ Je prépare ton lien Panier Famille…", ts: Date.now() });
    await new Promise((r) => setTimeout(r, 60));
    const fbStubIdx = Math.max(0, activeMessagesRef.current.length - 1);
    try {
      const r = await fetch(`${API}/v3/family-basket/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${fbToken}` },
        body: JSON.stringify({
          items: items.map((it) => ({ merchant_id: it.merchant_id, product_id: it.product_id, name: it.name, price_fcfa: it.price_fcfa, qty: it.qty })),
        }),
      });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.error || "création impossible");
      conv.updateMessage(active.id, fbStubIdx, (m) => ({
        ...m,
        content: `💛 **Panier Famille prêt !** Total : **${Number(d.total_fcfa).toLocaleString("fr-FR")} FCFA** (${items.length} article${items.length > 1 ? "s" : ""}).\n\nPartage ce lien à ton proche — il paie en 1 clic (carte bancaire ou wallet AYA), et dès le paiement la livraison part chez toi à Abidjan 🇨🇮`,
        family_basket: { basket_id: d.basket_id, share_path: d.share_path, total_fcfa: d.total_fcfa },
      }));
    } catch (e) {
      conv.updateMessage(active.id, fbStubIdx, (m) => ({ ...m, content: `❌ Impossible de créer le Panier Famille : ${e?.message || String(e)}` }));
    }
    return null;
  };

  const sendAgentV3 = async (agentMsg, agentOpts = {}) => {
    if (!agentMsg || !active) return null;
    const userBubble = { role: "user", content: agentMsg, ts: Date.now() };
    conv.appendMessage(active.id, userBubble);
    const stubBubble = { role: "agent", content: "⏳ Appel AYA agent…", ts: Date.now(), streaming: false };
    conv.appendMessage(active.id, stubBubble);
    await new Promise((r) => setTimeout(r, 60)); // Phase D.2 fix — laisse React flush les 2 setState avant lookup index.
    const stubIdx = Math.max(0, activeMessagesRef.current.length - 1);
    try {
      const resp = await fetch(`${API}/v3/agent/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: agentMsg, userId, sessionId: active.id,
          // v3.17.1 — Mémoire : l'orchestrateur reçoit le même historique que /agent/stream.
          history: conv.buildApiHistory(active),
          userName: authUser?.display_name?.split(" ")[0] || authUser?.first_name || null,
          selected_merchant_id: agentOpts.selectedMerchantId || null,
          selected_product_id: agentOpts.selectedProductId || null,
        }),
      });
      const data = await resp.json();
      const finalText = (data.text || data.error || "(réponse agent vide)") + (data.intent === "purchase" ? "\n\n_↗ Confirmation requise — modale ouverte._" : "");
      conv.updateMessage(active.id, stubIdx, (m) => ({ ...m, content: finalText, streaming: false, agent_meta: { intent: data.intent, entry_id: data.entry_id, llm_model: data.llm_model, response_type: data.response_type || 'text', ui_blocks: data.ui_blocks || null } }));
      if (data.intent === "purchase" && data.modal_payload) {
        setPendingPurchase(data.modal_payload);
      }
      // 2026-07-02 — Address Gate (§27.5) : carte address_creation dans la discussion.
      // La commande reprend AUTOMATIQUEMENT après création (resume_context.original_message)
      // EN PRÉSERVANT le choix utilisateur (selectedProductId/selectedMerchantId).
      if (data.status === "PREREQUISITE" && data.rx_payload?.template === "address_creation") {
        setAcGate({
          payload: data.rx_payload,
          originalMessage: data.rx_payload?.meta?.resume_context?.original_message || agentMsg,
          originalOpts: { selectedProductId: agentOpts.selectedProductId || null, selectedMerchantId: agentOpts.selectedMerchantId || null },
          status: "initial",
        });
      }
    } catch (e) {
      conv.updateMessage(active.id, stubIdx, (m) => ({ ...m, content: `❌ Erreur agent : ${e?.message || String(e)}`, streaming: false }));
    }
    return null;
  };

  // ── 2026-07-02 — Address Gate handlers (§27.5) ──────────────────────────────
  // Création d'adresse depuis le chat (GPS ou formulaire) → profil (code CIV
  // généré par le moteur d'adressage) → REPRISE AUTOMATIQUE de la commande.
  const _acSaveAddress = async (body) => {
    const _token = localStorage.getItem("civ_user_token");
    const r = await fetch(`${API}/profile/${userId}/addresses`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(_token ? { Authorization: `Bearer ${_token}` } : {}),
      },
      body: JSON.stringify(body),
    });
    const d = await r.json().catch(() => ({}));
    return { ok: r.ok && d.ok, data: d };
  };

  const _acFinishAndResume = (address, originalMessage, originalOpts = null) => {
    setAcGate((g) => (g ? { ...g, status: "success" } : g));
    toast.success(`Adresse ${address?.code || ""} enregistrée dans ton profil ✅`);
    setTimeout(() => {
      setAcGate(null);
      // Reprise auto EN PRÉSERVANT le choix utilisateur (produit/commerce sélectionné).
      if (originalMessage) sendMessageLive(originalMessage, originalOpts || {});
    }, 1500);
  };

  const handleAcGps = () => {
    const originalMessage = acGate?.originalMessage;
    const originalOpts = acGate?.originalOpts || null;
    if (!navigator.geolocation) { toast.error("GPS non disponible sur cet appareil"); return; }
    setAcGate((g) => (g ? { ...g, status: "validating" } : g));
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { ok, data } = await _acSaveAddress({
            name: "Domicile", label: "Domicile",
            lat: pos.coords.latitude, lng: pos.coords.longitude,
          });
          if (ok) { _acFinishAndResume(data.address, originalMessage, originalOpts); return; }
          if (data.error === "domicile_already_exists" || data.error === "name_already_exists") {
            _acFinishAndResume(data.address || {}, originalMessage, originalOpts); return;
          }
          setAcGate((g) => (g ? { ...g, status: "initial" } : g));
          toast.error(data.error || "Erreur d'enregistrement");
        } catch (_e) {
          setAcGate((g) => (g ? { ...g, status: "initial" } : g));
          toast.error("Erreur réseau — réessaie");
        }
      },
      () => {
        setAcGate((g) => (g ? { ...g, status: "initial" } : g));
        toast.error("Position refusée — autorise le GPS ou remplis le formulaire");
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const handleAcSubmit = async (form) => {
    const originalMessage = acGate?.originalMessage;
    const originalOpts = acGate?.originalOpts || null;
    setAcGate((g) => (g ? { ...g, status: "validating" } : g));
    try {
      const { ok, data } = await _acSaveAddress({
        name: "Domicile", label: form.label || "Domicile",
        commune: form.district, street_name: form.street,
      });
      if (ok) { _acFinishAndResume(data.address, originalMessage, originalOpts); return; }
      if (data.error === "domicile_already_exists" || data.error === "name_already_exists") {
        _acFinishAndResume(data.address || {}, originalMessage, originalOpts); return;
      }
      setAcGate((g) => (g ? { ...g, status: "initial" } : g));
      toast.error(data.error || "Erreur d'enregistrement");
    } catch (_e) {
      setAcGate((g) => (g ? { ...g, status: "initial" } : g));
      toast.error("Erreur réseau — réessaie");
    }
  };

  // ---- Core send logic — SSE streaming with chunked TTS + non-stream fallback ----
  const sendMessage = async (text, opts = {}) => {
    const trimmed = (text || "").trim();
    if (!trimmed || !active) return null;

    // v3.0.0-rc.7-phaseB-B33-ux-integration-fix-v1 — Routage agent v3 (Bug A fix) :
    //   1. Préfixe explicite `/agent <msg>` → forçage manuel (compat B.3.1, conservé pour debug + future Étape C).
    //   2. Détection regex purchase intent natif (sans préfixe) → route vers orchestrator v3 au lieu du flow legacy.
    //   Cela résout Bug A B.3.3 : usage réel utilisateur sans préfixe ne déclenchait pas la modale purchase.
    if (trimmed.startsWith("/agent ")) {
      const agentMsg = trimmed.slice(7).trim();
      return sendAgentV3(agentMsg);
    }
    // v3.20.0 — GPS conversationnel (sauf codes CIV-XXX : flux backend existant).
    if (!/\bCIV-/i.test(trimmed)) {
      const gpsMatch = trimmed.match(GPS_NAV_RE);
      if (gpsMatch && gpsMatch[1]) {
        return sendGpsNav(trimmed, gpsMatch[1].trim().replace(/[?!.]+$/, ""));
      }
    }
    // v3.24.0 — "mes réservations" AVANT le détecteur beauté ("réserve" y figure).
    if (MY_BOOKINGS_RE.test(trimmed)) {
      return sendMyBookings(trimmed);
    }
    // v3.23.0 — Beauté chat : "trouve-moi un barber" → salons direct.
    {
      const _bea = detectBeaute(trimmed);
      if (_bea) return sendBeauteSuggest(trimmed, _bea);
    }
    // v3.22.0 — Food chat : "je veux un garba" → adresses Food direct.
    {
      const _dish = detectFoodDish(trimmed);
      if (_dish) return sendFoodDish(trimmed, _dish);
    }
    // v3.18.0 — Espace Diaspora : "mes demandes" AVANT le court-circuit panier
    // famille ("mes paniers famille" = consultation, pas création).
    if (DIASPORA_REQ_RE.test(trimmed)) {
      return sendDiasporaRequests(trimmed);
    }
    // v3.17.1 — Panier Famille (diaspora) : court-circuit AVANT le routage produit/achat.
    if (FAMILY_BASKET_RE.test(trimmed)) {
      return sendFamilyBasket(trimmed);
    }
    // 2026-07-02 — Clic carte « Commander » : produit/commerce CHOISI PAR L'UTILISATEUR
    // (selected_product_id / selected_merchant_id) → preview directe pour CE choix.
    if (opts.selectedProductId || opts.selectedMerchantId) {
      return sendAgentV3(trimmed, { selectedProductId: opts.selectedProductId || null, selectedMerchantId: opts.selectedMerchantId || null });
    }
    if (PRODUCT_SEARCH_RE.test(trimmed) || _matchesDynamicProduct(trimmed)) {
      console.log("[sendMessage] product search detected → routing to orchestrator v3 (marketplace)");
      return sendAgentV3(trimmed);
    }
    // 2026-07-08 (owner) — RECHERCHES COMPLEXES (immobilier, véhicule) : AYA a
    // besoin de détails → carte de critères DÉTERMINISTE (registre déclaratif),
    // sans aller-retour backend. La requête enrichie repart en flux normal.
    const _criteriaDomain =
      (/\b(appartements?|studios?|villas?|maisons?|terrains?)\b/i.test(trimmed) && /\b(cherche|trouve|veux|voudrais|louer|acheter|besoin)\b/i.test(trimmed) && !/chambre|budget|fcfa/i.test(trimmed)) ? "immobilier"
      : (/\b(voitures?|v[ée]hicules?|motos?|automobiles?)\b/i.test(trimmed) && /\b(cherche|trouve|veux|voudrais|acheter|besoin)\b/i.test(trimmed) && !/budget|fcfa|marque/i.test(trimmed)) ? "vehicule"
      : null;
    if (_criteriaDomain) {
      conv.appendMessage(active.id, { role: "user", content: trimmed, ts: Date.now() });
      conv.appendMessage(active.id, {
        role: "agent",
        content: "Bonne idée 👌 Donne-moi 2-3 détails pour que je cherche exactement ce qu'il te faut :",
        ts: Date.now() + 1,
        criteria_form: _criteriaDomain,
      });
      return null;
    }
    // 2026-06 — AYA PRIVATE ACTION ENGINE V1 : les actions personnelles (taxi,
    // billets, factures, hôtels, places de car, rendez-vous, mes adresses) vont
    // DIRECTEMENT au backend /agent (orchestrateur d'actions), pas au flux achat.
    const PRIVATE_ACTION_RE = /\b(taxi|chauffeur|place?s?\s+pour|h[ôo]tel|facture|re[çc]u|billet|rendez[- ]?vous|rdv|mes\s+adresses|derni[èe]re?\s+(commande|facture)|a[ée]roport|trajet|organise\s+mon)\b/i;
    if (PURCHASE_INTENT_RE.test(trimmed) && !PRIVATE_ACTION_RE.test(trimmed)) {
      console.log("[sendMessage] purchase intent detected → routing to orchestrator v3");
      return sendAgentV3(trimmed);
    }

    const source = opts.source || (opts.voice ? "voice" : "text");
    console.log("[sendMessage]", { source, mode, voice: !!opts.voice, len: trimmed.length });

    // ---- Abort any in-flight stream BEFORE starting a new one (Bug fix: orphaned tiny bubbles) ----
    if (abortCtrlRef.current) {
      console.log("[sendMessage] aborting previous stream");
      try { abortCtrlRef.current.abort(); } catch (_) {}
      abortCtrlRef.current = null;
    }
    // Clean orphan agent stub: last message that is streaming with < 5 chars
    // Skip when regenerating into an existing bubble (replaceIndex set).
    let removedOrphan = 0;
    if (typeof opts.replaceIndex !== "number") {
      const msgs = active.messages || [];
      const lastIdx = msgs.length - 1;
      if (lastIdx >= 0) {
        const last = msgs[lastIdx];
        if (last?.role === "agent" && last?.streaming && (last.content || "").length < 5) {
          console.log("[sendMessage] removing orphan stream stub at index", lastIdx);
          conv.removeMessage(active.id, lastIdx);
          removedOrphan = 1;
        } else if (last?.role === "agent" && last?.streaming) {
          conv.updateMessage(active.id, lastIdx, (m) => ({ ...m, streaming: false, interrupted: true }));
        }
      }
    }
    stopAllAudio();

    interruptedRef.current = false;
    const history = conv.buildApiHistory(active);
    const wantVoice = opts.voice === true && voiceOutputEnabled;
    let agentIndex;
    if (typeof opts.replaceIndex === "number") {
      // Regenerate: stream into an existing bubble (already reset by caller).
      agentIndex = opts.replaceIndex;
    } else {
      const userMsg = { role: "user", content: trimmed, ts: Date.now() };
      conv.appendMessage(active.id, userMsg);
      const agentMsg = { role: "agent", content: "", ts: Date.now(), streaming: true, voice: wantVoice, premium: opts.premium || undefined };
      conv.appendMessage(active.id, agentMsg);
      // The new agent message lives at: original_length - removedOrphan + 1 (after the user we just appended).
      agentIndex = (active.messages?.length || 0) - removedOrphan + 1;
    }
    setLoading(true);
    setLoadingPhase("listening");
    setStreamingActive(true);

    const conversationId = active.id;

    const supportsStreaming =
      typeof window !== "undefined" &&
      typeof window.fetch === "function" &&
      typeof TextDecoder !== "undefined" &&
      typeof window.AbortController === "function";

    // ---- Streaming path ----
    if (supportsStreaming) {
      const ctrl = new AbortController();
      abortCtrlRef.current = ctrl;
      const timeoutId = setTimeout(() => ctrl.abort(), 30000);
      try {
        const res = await fetch(`${API}/agent/stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: trimmed,
            userId,
            conversationId,
            mode: opts.modeOverride || mode,
            voice: wantVoice,
            voice_streaming: wantVoice, // request chunked TTS when voice on
            web_search: opts.force_web || webSearchMode,
            include_domains: Array.isArray(opts.include_domains) ? opts.include_domains : undefined,
            response_lang: responseLang,
            history,
            // v2.19.24 — Doc/image rattaché à ce message (ordonnance ou attach normal).
            doc_id: opts.docId || attachedDoc?.doc_id || undefined,
          }),
          signal: ctrl.signal,
        });
        if (!res.ok || !res.body) throw new Error(`stream_http_${res.status}`);

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let accumulated = "";

        const handleEvent = (eventName, dataStr) => {
          let data;
          try { data = JSON.parse(dataStr); } catch (_) { return; }
          if (eventName === "token") {
            // v2.19.20 — Premier token reçu → phase streaming.
            setLoadingPhase((p) => (p === "streaming" ? p : "streaming"));
            accumulated += data.text || "";
            conv.updateMessage(conversationId, agentIndex, (m) => ({
              ...m, content: accumulated, streaming: true,
            }));
            clearTimeout(timeoutId);
          } else if (eventName === "token_done") {
            accumulated = data.full_text || accumulated;
            conv.updateMessage(conversationId, agentIndex, (m) => ({
              ...m, content: accumulated, streaming: false,
            }));
          } else if (eventName === "voice_chunk") {
            enqueueAudioChunk(data);
          } else if (eventName === "voice") {
            // legacy block-mode voice
            enqueueAudioChunk({ seq: 0, audio_base64: data.audio_base64, mimeType: data.mimeType });
          } else if (eventName === "voice_warning" || eventName === "voice_chunk_error") {
            conv.updateMessage(conversationId, agentIndex, (m) => ({
              ...m, voiceWarning: data.warning || data.error || "voice unavailable",
            }));
          } else if (eventName === "web_search") {
            // v2.19.20 — RAG/Tavily en cours.
            setLoadingPhase("rag_fetching");
            conv.updateMessage(conversationId, agentIndex, (m) => ({
              ...m,
              webSearch: {
                triggered: !!data.triggered,
                query: data.query || null,
                sources: Array.isArray(data.sources) ? data.sources : [],
                duration_ms: data.duration_ms || null,
                cache_hit: !!data.cache_hit,
                error: data.error || null,
              },
            }));
          } else if (eventName === "sponsored") {
            conv.updateMessage(conversationId, agentIndex, (m) => ({
              ...m, sponsored: data,
            }));
          } else if (eventName === "sponsored_cards") {
            // v1.13.0 — partner recommendation cards from the SSE stream.
            conv.updateMessage(conversationId, agentIndex, (m) => ({
              ...m,
              sponsored_cards: Array.isArray(data?.cards) ? data.cards : null,
              sponsored_disclosure: data?.disclosure || null,
              sponsored_meta: data ? {
                category: data.category, subcategory: data.subcategory,
                intent: data.intent, trigger_mode_used: data.trigger_mode_used,
              } : null,
            }));
          } else if (eventName === "media") {
            // v1.16.0 — RAG media (images/maps/links) attachées à la bulle.
            // v2.0.0 — Support `commerce_card` type aussi.
            conv.updateMessage(conversationId, agentIndex, (m) => ({
              ...m,
              rag_media: Array.isArray(data?.media) ? data.media : null,
            }));
          } else if (eventName === "ui_components") {
            // v2.14.0 — UI components du function calling (map, transport, place_list).
            // v2.19.20 — Tool exécuté → phase tools_calling.
            setLoadingPhase("tools_calling");
            conv.updateMessage(conversationId, agentIndex, (m) => ({
              ...m,
              ui_components: Array.isArray(data?.ui_components) ? data.ui_components : null,
            }));
          } else if (eventName === "meta") {
            // v2.0.0 — Si meta porte requires_geolocation, on déclenche le flow address.
            if (data && data.requires_geolocation && data.action === "generate_address") {
              runAddressFlow(conversationId, agentIndex);
            }
          } else if (eventName === "translation_complete") {
            conv.updateMessage(conversationId, agentIndex, (m) => ({
              ...m,
              content: data.translated_text || m.content,
              translation: {
                target_lang: data.target_lang,
                original_fr: data.original_fr,
                lexicon_id: data.lexicon_id,
                nllb_latency_ms: data.nllb_latency_ms,
                cache_hit: !!data.cache_hit,
                showOriginal: false,
              },
            }));
          } else if (eventName === "translation_error") {
            conv.updateMessage(conversationId, agentIndex, (m) => ({
              ...m,
              translation: {
                target_lang: data.target_lang,
                error: data.error,
                estimated_time: data.estimated_time,
              },
            }));
          } else if (eventName === "error") {
            const msg = `Bloqué : ${data.reason || "?"}${data.details ? " — " + data.details : ""}`;
            conv.updateMessage(conversationId, agentIndex, () => ({
              role: "blocked", content: msg, ts: Date.now(), streaming: false,
            }));
          }
        };

        const flushBuffer = () => {
          let sep;
          while ((sep = buffer.indexOf("\n\n")) !== -1) {
            const block = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);
            const lines = block.split("\n");
            let evName = "message";
            const dataLines = [];
            for (const ln of lines) {
              if (ln.startsWith("event:")) evName = ln.slice(6).trim();
              else if (ln.startsWith("data:")) dataLines.push(ln.slice(5).trim());
            }
            if (dataLines.length) handleEvent(evName, dataLines.join("\n"));
          }
        };

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          flushBuffer();
        }
        clearTimeout(timeoutId);
        // Cleanup streaming state
        conv.updateMessage(conversationId, agentIndex, (m) => ({ ...m, streaming: false }));
        abortCtrlRef.current = null;
        setStreamingActive(false);
        return { ok: true, interrupted: interruptedRef.current };
      } catch (err) {
        clearTimeout(timeoutId);
        abortCtrlRef.current = null;
        setStreamingActive(false);
        // v2.19.2d — reset audioActive aussi sur stream error/abort pour ne pas
        // laisser le bouton "Interrompre" visible.
        setAudioActive(false);
        if (err.name === "AbortError" || interruptedRef.current) {
          // user interrupted — leave message as-is, mark not streaming
          conv.updateMessage(conversationId, agentIndex, (m) => ({ ...m, streaming: false, interrupted: true }));
          return { interrupted: true };
        }
        console.warn("[stream] failed, falling back to non-stream:", err.message);
      } finally {
        setLoading(false);
      }
    }

    // ---- Fallback: non-streaming ----
    setLoading(true);
    setLoadingPhase("listening");
    try {
      const res = await axios.post(`${API}/agent`, {
        message: trimmed, userId, conversationId, mode, voice: wantVoice,
        web_search: opts.force_web || webSearchMode,
        include_domains: Array.isArray(opts.include_domains) ? opts.include_domains : undefined,
        response_lang: responseLang,
        history,
        // v2.19.21e — doc attaché via la trombone (TTL backend 15 min).
        doc_id: attachedDoc?.doc_id || undefined,
      });
      const data = res.data || {};
      if (data.status === "BLOCKED") {
        const g = data.governance || {};
        // v2.19.4a — Si le backend fournit des détails FR friendly (cas LLM
        // upstream HS / budget exceeded), on les affiche TELS QUELS sans
        // préfixe technique "Bloqué par gouvernance: <reason> —". Sinon
        // (autres reasons comme finance_topic, etc.) on garde le format précédent.
        const isUpstreamFail = g.reason === "llm_unavailable_and_blocked_by_manifest";
        const reasonText = isUpstreamFail && g.details
          ? g.details
          : (g.reason ? `Bloqué par gouvernance : ${g.reason}${g.details ? ` — ${g.details}` : ""}` : (data.reason || "Bloqué par JSONFIRST guard."));
        conv.updateMessage(conversationId, agentIndex, () => ({
          role: "blocked",
          content: reasonText,
          ts: Date.now(),
          streaming: false,
          retryable: isUpstreamFail || g.reason === "llm_upstream_error",
          lastUserMessage: text,
        }));
        return { blocked: true };
      }
      if (data.status === "ERROR") {
        conv.updateMessage(conversationId, agentIndex, () => ({
          role: "error", content: data.error || "Erreur serveur.", ts: Date.now(), streaming: false,
        }));
        return { error: true };
      }
      const audioB64 = data.voice?.audio || null;
      const mimeType = data.voice?.mimeType || "audio/mpeg";
      const govWS = data.governance?.web_search;
      const govTr = data.governance?.translation;
      conv.updateMessage(conversationId, agentIndex, () => ({
        role: "agent",
        content: data.text || "(réponse vide)",
        ts: Date.now(),
        voiceWarning: data.voice_warning || null,
        streaming: false,
        webSearch: govWS && (govWS.triggered || govWS.error) ? {
          triggered: !!govWS.triggered,
          query: govWS.query || null,
          sources: Array.isArray(govWS.sources) ? govWS.sources : [],
          duration_ms: govWS.duration_ms || null,
          cache_hit: !!govWS.cache_hit,
          error: govWS.error || null,
        } : undefined,
        sponsored: data.sponsored || undefined,
        sponsored_cards: Array.isArray(data.sponsored_cards) ? data.sponsored_cards : undefined,
        sponsored_disclosure: data.sponsored_disclosure || undefined,
        sponsored_meta: data.sponsored_meta || undefined,
        rag_media: Array.isArray(data.media) ? data.media : undefined,
        // v2.14.0 — UI components du function calling (map, transport_buttons, place_list, …).
        ui_components: Array.isArray(data.ui_components) ? data.ui_components : undefined,
        translation: govTr && (govTr.target_lang || govTr.error) ? {
          target_lang: govTr.target_lang,
          original_fr: govTr.original_fr,
          lexicon_id: govTr.lexicon_id,
          nllb_latency_ms: govTr.nllb_latency_ms,
          cache_hit: !!govTr.cache_hit,
          error: govTr.error,
          showOriginal: false,
        } : undefined,
        premium: opts.premium || undefined,
      }));
      if (audioB64) enqueueAudioChunk({ seq: 0, audio_base64: audioB64, mimeType });
      // v2.0.0 — Si l'agent a demandé la géoloc (intent mon_adresse), on lance le flow.
      if (data.requires_geolocation && data.action === "generate_address") {
        runAddressFlow(conversationId, agentIndex);
      }
      return { ok: true };
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Erreur réseau inconnue.";
      conv.updateMessage(conversationId, agentIndex, () => ({
        role: "error", content: `Erreur: ${msg}`, ts: Date.now(), streaming: false,
      }));
      return { error: true };
    } finally {
      setLoading(false);
      setStreamingActive(false);
      // v2.19.2d — reset audioActive aussi pour ne pas laisser le bouton
      // "⏹️ Interrompre" visible après une erreur (ex: 404 d'un tool LLM).
      setAudioActive(false);
    }
  };

  // 2026-07-02 — Fix "première requête perdue" (closure périmée).
  // handleSend & plusieurs handlers sont des useCallback à dépendances minimales :
  // ils capturaient une version OBSOLÈTE de sendMessage (donc de `active`).
  // Conséquence : la 1ère requête d'une nouvelle conversation partait vers
  // l'ancienne conversation (ou était silencieusement abandonnée), la 2ème
  // fonctionnait (recréation de la closure après le flip de `loading`).
  // sendMessageLive délègue TOUJOURS à la dernière version rendue de sendMessage.
  const sendMessageRef = useRef(null);
  sendMessageRef.current = sendMessage;
  const sendMessageLive = useCallback((...args) => sendMessageRef.current?.(...args), []);

  // 2026-09 — Deep-link contexte AYA Privé : /aya-prive?ctx=<message> (ex : contenu
  // sponsorisé Kpandji depuis /search) → le message est envoyé au Conversation Engine.
  const _ctxLinkHandled = useRef(false);
  useEffect(() => {
    if (_ctxLinkHandled.current) return;
    const sp = new URLSearchParams(window.location.search);
    const ctx = sp.get("ctx");
    if (!ctx) return;
    if (!authUser) return;
    _ctxLinkHandled.current = true;
    window.history.replaceState({}, "", window.location.pathname);
    setTimeout(() => { sendMessageLive(ctx); }, 1200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);

  // 2026-07-03 — Deep-link depuis le MINI-SITE marchand : /?buy=<product_id>&m=<merchant_id>
  // → commande immédiate avec le CHOIX PRÉSERVÉ (selectedProductId → preview du produit).
  // Patient : si l'utilisateur n'est pas encore connecté, on attend l'authentification.
  const _buyLinkHandled = useRef(false);
  useEffect(() => {
    if (_buyLinkHandled.current) return;
    const sp = new URLSearchParams(window.location.search);
    const buy = sp.get("buy");
    if (!buy) return;
    if (!authUser) return; // le modal d'auth est ouvert — on retentera après connexion
    _buyLinkHandled.current = true;
    const label = sp.get("label") || "ce produit";
    const price = sp.get("price") || "";
    const mname = sp.get("mname") || "la boutique";
    const mid = sp.get("m") || null;
    window.history.replaceState({}, "", "/");
    setTimeout(() => {
      sendMessageLive(
        `Commande : ${label} chez ${mname}${price ? ` — ${price} FCFA` : ""}`,
        { selectedProductId: buy, selectedMerchantId: mid }
      );
    }, 1500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);


  // v2.19.2c — Templates de textes d'intro contextuels par catégorie/sous-cat
  // (utilisés par TOUS les handlers de click sur cards, pour éviter d'appeler le LLM).
  const QUOTIDIEN_INTRO = {
    live:           "🔴 Voici les events en direct à Abidjan 🇨🇮",
    news:           "📰 Voici les actualités à la une.",
    mode:           "👗 Voici les boutiques mode & wax tendance.",
    "beauté":       "💄 Voici les meilleures adresses beauté CIV.",
    "marché":       "🛒 Voici les marketplaces & bons plans du moment.",
    tech:           "💻 Voici l'écosystème tech ivoirien.",
    sport:          "⚽ Voici l'actu sport & événements sportifs.",
    culture:        "🎭 Voici l'actu culture & événements.",
    business:       "💼 Voici les médias business & PME.",
    mobile_money:   "📱 Voici les opérateurs Mobile Money.",
    abidjan_vibes:  "🌴 Voici l'ambiance lifestyle d'Abidjan.",
  };

  // ---- Text input ----
  // v1.20.2 — Auto-send au clic catégorie (plus de pré-remplissage qui bloque).
  const insertCategoryPrompt = (cat) => {
    const prompt = `Parle-moi de ${prettyLabel(cat)} en Côte d'Ivoire`;
    const wantTtsAuto = voiceOutputEnabled && mode !== "casual";
    sendMessageLive(prompt, wantTtsAuto ? { voice: true } : {});
  };

  // v2.19.2c — Refonte du onCategoryClick (mode Quotidien) :
  // au lieu de basculer en "discovery mode" (FeaturedCards en sous-onglet au-dessus
  // du chat), on pousse directement une bulle agent locale `kind:"system_quick_search"`
  // dans la conversation avec un texte d'intro contextuel + rag_media `featured_items_grid`
  // qui rend les items via FeaturedCards inline. AUCUN appel LLM, AUCUNE bulle user.
  const partnersByCategory = (cat) => partners.filter((p) => p.category === cat);
  // v3.0.1 — FIX "chaque intention devient un écran" : au lieu d'EMPILER une
  // nouvelle bulle à chaque clic de catégorie, on REMPLACE la dernière bulle
  // si c'est déjà une quick-search locale (comportement onglet, pas pile).
  const pushQuickSearch = (convId, msg) => {
    const msgs = activeMessagesRef.current || [];
    const lastIdx = msgs.length - 1;
    if (lastIdx >= 0 && msgs[lastIdx]?.kind === "system_quick_search") {
      conv.updateMessage(convId, lastIdx, () => msg);
    } else {
      conv.appendMessage(convId, msg);
    }
  };
  const onCategoryClick = async (cat) => {
    if (!active?.id) return;
    setActiveCategory(cat); // garde l'état actif pour le styling éventuel
    const intro = QUOTIDIEN_INTRO[cat] || `Voici les actualités ${prettyLabel(cat).toLowerCase()} en Côte d'Ivoire.`;
    let items = [];
    let categoryImage = getCategoryImage(cat) || {};
    if (cat === "live") {
      // Fetch live events depuis backend (ou réutilise le cache liveEvents).
      try {
        const r = await axios.get(`${API}/live/events`, { params: { sub_category: liveSubCat } });
        items = Array.isArray(r.data?.events) ? r.data.events : [];
      } catch (_) {
        items = liveEvents || [];
      }
      categoryImage = getCategoryImage("live") || {};
    } else {
      items = getFeaturedItems(cat) || [];
    }
    conv.appendMessage(active.id, {
      role: "agent",
      content: intro,
      ts: Date.now(),
      kind: "system_quick_search",
      isLocal: true,
      rag_media: [{
        type: "featured_items_grid",
        data: { category: cat, items, categoryImage, isLive: cat === "live" },
      }],
    });
  };

  // v2.7.1 — Au clic sur une chip Justice : fetch les profils et pousser un
  // message bot avec les cards (pas d'appel LLM).
  // v2.19.2c — Plus de bulle user (cf demande utilisateur). kind+isLocal pour
  // ne pas marquer "(interrompu)".
  const onJusticeCategoryClick = async (jc) => {
    if (!active?.id) return;
    try {
      const r = await axios.get(`${API}/justice/categories/${jc.id}/profiles?limit=10`);
      const data = r.data || {};
      const profiles = Array.isArray(data.profiles) ? data.profiles : [];
      pushQuickSearch(active.id, {
        role: "agent",
        content: `⚖️ Voici ${profiles.length} ${jc.label.toLowerCase()} disponibles. Clique "🧭 Y aller" pour naviguer ou "📞 Appeler" pour contacter directement.`,
        ts: Date.now(),
        kind: "system_quick_search",
        isLocal: true,
        rag_media: [
          { type: "justice_grid", data: { category: jc, profiles } },
          { type: "detail_prompt", data: { prompt_id: `dp-${Date.now()}`, domain: "justice", category_id: jc.id, category_label: jc.label } },
        ],
      });
    } catch (e) {
      toast.error("Impossible de charger les profils Justice");
    }
  };

  // v2.10.0 — Au clic sur une chip Santé : symétrique de Justice.
  // v2.19.2c — Plus de bulle user.
  const onHealthCategoryClick = async (hc) => {
    if (!active?.id) return;
    try {
      const r = await axios.get(`${API}/health/categories/${hc.id}/profiles?limit=10`);
      const data = r.data || {};
      const profiles = Array.isArray(data.profiles) ? data.profiles : [];
      const urgent = hc.id === "urgence_24_24";
      const intro = urgent
        ? `🚨 ${profiles.length} services d'urgence disponibles 24h/24. Appel direct depuis chaque card.`
        : `🏥 Voici ${profiles.length} fiches ${hc.label.toLowerCase()}. Boutons : 🧭 Y aller / 📞 Appeler${profiles.some((p) => p.telephone_urgence) ? " / 🚨 Urgence" : ""} / 📋 Copier.`;
      pushQuickSearch(active.id, {
        role: "agent",
        content: intro,
        ts: Date.now(),
        kind: "system_quick_search",
        isLocal: true,
        rag_media: [
          { type: "health_grid", data: { category: hc, profiles } },
          { type: "detail_prompt", data: { prompt_id: `dp-${Date.now()}`, domain: "sante", category_id: hc.id, category_label: hc.label } },
        ],
      });
    } catch (e) {
      toast.error("Impossible de charger les profils Santé");
    }
  };

  // v2.17.0 — Handlers Administration + Finance.
  // v2.19.2c — Plus de bulle user.
  const onDirectoryCategoryClick = async (cat, kind) => {
    if (!active?.id) return;
    const cfg = kind === "administration"
      ? { endpoint: "v3/administration", label: "Administration", emojiPrefix: "🛂", grid: "admin_grid" }
      : { endpoint: "v3/finance", label: "Finance", emojiPrefix: "💰", grid: "finance_grid" };
    try {
      const r = await axios.get(`${API}/${cfg.endpoint}?category=${cat.id}&limit=12`);
      const items = Array.isArray(r.data?.items) ? r.data.items : [];
      const intro = items.length === 0
        ? `${cfg.emojiPrefix} Aucune fiche pour ${cat.label.toLowerCase()}.`
        : `${cfg.emojiPrefix} Voici ${items.length} ${kind === "administration" ? "démarches" : "services"} « ${cat.label.toLowerCase()} » en Côte d'Ivoire. Clique 🌐 Site pour visiter, 📞 pour appeler, 📄 Page dédiée pour les détails.`;
      pushQuickSearch(active.id, {
        role: "agent", content: intro, ts: Date.now(),
        kind: "system_quick_search",
        isLocal: true,
        rag_media: [
          { type: cfg.grid, data: { category: cat, items } },
          { type: "detail_prompt", data: { prompt_id: `dp-${Date.now()}`, domain: kind, category_id: cat.id, category_label: cat.label } },
        ],
      });
    } catch (e) {
      toast.error(`Impossible de charger les fiches ${kind}`);
    }
  };
  const onAdministrationCategoryClick = (c) => onDirectoryCategoryClick(c, "administration");
  const onFinanceCategoryClick = (c) => onDirectoryCategoryClick(c, "finance");

  // v2.19.10 — Shopping click : bulle agent LOCALE (pas d'appel LLM).
  // Aligné sur le pattern v2.19.2c (Justice/Santé/Admin/Finance) : on push
  // une bulle "system_quick_search" avec intro + place_list. Si le directory
  // shopping est vide (cas actuel — catalogue en cours d'enrichissement),
  // on affiche un message friendly sans toucher au LLM.
  const SHOPPING_INTRO_TEXTS = {
    food: "🍽 Voici les meilleurs restos et cantines food d'Abidjan.",
    cuisine: "🍳 Voici les boutiques ustensiles & équipement cuisine.",
    art_deco: "🎨 Voici les adresses art & déco africaine.",
    vet_homme: "👔 Voici les boutiques vêtements homme à Abidjan.",
    vet_femme: "👗 Voici les boutiques vêtements femme à Abidjan.",
    vet_enfant: "👕 Voici les boutiques vêtements enfants.",
    chaussures: "👟 Voici les magasins de chaussures à Abidjan.",
    bijoux: "💍 Voici les bijouteries à Abidjan.",
    quincaillerie: "🔨 Voici les quincailleries à Abidjan.",
    electromenager: "🔌 Voici les magasins électroménager à Abidjan.",
    tech: "📱 Voici les boutiques tech & électronique.",
    beaute: "💄 Voici les boutiques beauté & cosmétique.",
    supermarche: "🛒 Voici les supermarchés à Abidjan.",
    marche: "🥬 Voici les marchés traditionnels d'Abidjan.",
    pharmacie_para: "💊 Voici les para-pharmacies à Abidjan.",
    papeterie: "📚 Voici les papeteries & librairies.",
    jouets: "🧸 Voici les magasins de jouets enfants.",
    sport: "⚽ Voici les magasins sport & fitness.",
    auto: "🚗 Voici les boutiques auto & moto.",
    agro: "🌾 Voici les agro & jardinage.",
  };
  const onShoppingCategoryClick = async (sc) => {
    if (!active?.id) return;
    const intro = SHOPPING_INTRO_TEXTS[sc.id] || `🛍 Voici les adresses ${(sc.label || "").toLowerCase()} à Abidjan.`;
    // Tentative directory shopping (endpoint pas encore live → fallback empty).
    let items = [];
    try {
      const r = await axios.get(`${API}/v3/shopping?category=${sc.id}&limit=12`);
      items = Array.isArray(r.data?.items) ? r.data.items : [];
    } catch (_) {
      items = [];
    }
    if (items.length === 0) {
      conv.appendMessage(active.id, {
        role: "agent",
        content: "🛍 Le catalogue Shopping pour cette catégorie est en cours d'enrichissement. Reviens bientôt !",
        ts: Date.now(),
        kind: "system_quick_search",
        isLocal: true,
      });
      return;
    }
    pushQuickSearch(active.id, {
      role: "agent",
      content: intro,
      ts: Date.now(),
      kind: "system_quick_search",
      isLocal: true,
      rag_media: [{ type: "shopping_grid", data: { category: sc, items } }],
    });
  };

  // v2.16.2 — Au clic sur une chip Emploi.
  // v2.19.2c — Plus de bulle user.
  const onEmploiCategoryClick = async (ec) => {
    if (!active?.id) return;
    try {
      const r = await axios.get(`${API}/v3/emploi?category=${ec.id}&limit=10`);
      const data = r.data || {};
      const items = Array.isArray(data.items) ? data.items : [];
      const platforms = [...new Set(items.map((i) => i.platform_name))];
      const intro = items.length === 0
        ? `💼 Aucune offre disponible pour ${ec.label.toLowerCase()} pour le moment.`
        : `💼 Voici ${items.length} offres « ${ec.label.toLowerCase()} » agrégées depuis ${platforms.length} plateforme${platforms.length > 1 ? "s" : ""} (${platforms.join(", ")}). Clique 🔗 Postuler pour aller directement sur la plateforme ou 📄 Page dédiée pour le détail complet.`;
      pushQuickSearch(active.id, {
        role: "agent", content: intro, ts: Date.now(),
        kind: "system_quick_search",
        isLocal: true,
        rag_media: [
          { type: "emploi_grid", data: { category: ec, items } },
          { type: "detail_prompt", data: { prompt_id: `dp-${Date.now()}`, domain: "emploi", category_id: ec.id, category_label: ec.label } },
        ],
      });
    } catch (e) {
      toast.error("Impossible de charger les offres Emploi");
    }
  };

  // v2.16.0 — Au clic sur une chip Divertissement.
  // v2.19.2c — Plus de bulle user.
  // v3.21.0 — Clic onglet Food : 13 adresses (1 par commune) en cartes grid.
  const onFoodCategoryClick = async (fc) => {
    if (!active?.id) return;
    try {
      const r = await axios.get(`${API}/v3/food?category=${fc.id}&limit=13`);
      const data = r.data || {};
      let items = Array.isArray(data.items) ? data.items : [];
      // v3.22.0 — Filtre "Ma Commune" : adresses proches en tête + badge.
      const _cc = await getUserCommuneCode();
      items = tagNearYou(items, _cc);
      const sponsoredCount = items.filter((i) => i.sponsored_tier !== "free" && i.platform_offer).length;
      const intro = `🍽️ Voici ${items.length} adresses « ${fc.label.toLowerCase()} » — une par commune d'Abidjan${sponsoredCount > 0 ? ` (dont ${sponsoredCount} avec offres AYA 🎁)` : ""}. Clique 🚀 GPS pour naviguer.`;
      pushQuickSearch(active.id, {
        role: "agent",
        content: intro,
        ts: Date.now(),
        kind: "system_quick_search",
        isLocal: true,
        rag_media: [
          { type: "div_grid", data: { category: fc, items } },
          { type: "detail_prompt", data: { prompt_id: `dp-${Date.now()}`, domain: "food", category_id: fc.id, category_label: fc.label } },
        ],
      });
    } catch (e) {
      toast.error("Impossible de charger les adresses Food");
    }
  };

  // v3.22.0 — Clic onglet Beauté : 13 salons (1 par commune), proches en tête.
  const onBeauteCategoryClick = async (bc) => {
    if (!active?.id) return;
    try {
      const r = await axios.get(`${API}/v3/beaute?category=${bc.id}&limit=13`);
      let items = Array.isArray(r.data?.items) ? r.data.items : [];
      const cc = await getUserCommuneCode();
      items = tagNearYou(items, cc);
      const near = items.filter((i) => i.near_you).length;
      const intro = `💅 Voici ${items.length} adresses « ${bc.label.toLowerCase()} » — une par commune d'Abidjan${near > 0 ? " (celles de ta commune d'abord 📍)" : ""}. Clique 🚀 GPS pour y aller.`;
      pushQuickSearch(active.id, {
        role: "agent", content: intro, ts: Date.now(), kind: "system_quick_search", isLocal: true,
        rag_media: [
          { type: "div_grid", data: { category: bc, items } },
          { type: "detail_prompt", data: { prompt_id: `dp-${Date.now()}`, domain: "beaute", category_id: bc.id, category_label: bc.label } },
        ],
      });
    } catch (e) {
      toast.error("Impossible de charger les adresses Beauté");
    }
  };

  const onDivCategoryClick = async (dc) => {
    if (!active?.id) return;
    try {
      const r = await axios.get(`${API}/v3/divertissement?category=${dc.id}&limit=10`);
      const data = r.data || {};
      const items = Array.isArray(data.items) ? data.items : [];
      const sponsoredCount = items.filter((i) => i.sponsored_tier !== "free" && i.platform_offer).length;
      const intro = `🎉 Voici ${items.length} adresses « ${dc.label.toLowerCase()} » à Abidjan${sponsoredCount > 0 ? ` (dont ${sponsoredCount} avec offres AYA 🎁)` : ""}. Clique 🚀 GPS pour naviguer ou 🎁 Offre AYA pour le code promo.`;
      pushQuickSearch(active.id, {
        role: "agent",
        content: intro,
        ts: Date.now(),
        kind: "system_quick_search",
        isLocal: true,
        rag_media: [
          { type: "div_grid", data: { category: dc, items } },
          { type: "detail_prompt", data: { prompt_id: `dp-${Date.now()}`, domain: "divertissement", category_id: dc.id, category_label: dc.label } },
        ],
      });
    } catch (e) {
      toast.error("Impossible de charger les adresses Divertissement");
    }
  };

  // Per-type partner card handlers (refonte v1.8.1).
  // LEFT button → /api/partners/:id/bio → templated query routed through chat with Tavily scope.
  // RIGHT button → newspaper opens payment modal; all other types open URL or Google Maps.
  const handlePartnerLeftClick = async (partner) => {
    setCarouselCategory(null);
    try {
      const r = await axios.post(`${API}/partners/${partner.id}/bio`);
      const data = r.data || {};
      const query = data.suggested_query || `Parle-moi de ${partner.name}`;
      const include = (data.web_search?.include_domains) || [partner.domain].filter(Boolean);
      sendMessageLive(query, { include_domains: include, force_web: "on" });
    } catch (e) {
      // Defensive fallback: if /bio fails, use the legacy templated query.
      const text = `Donne-moi les ${categoryReadVerb(partner.category)} récents de ${partner.name} aujourd'hui`;
      sendMessageLive(text, { include_domains: [partner.domain].filter(Boolean), force_web: "on" });
    }
  };

  const handlePartnerRightClick = (partner) => {
    if (partner.type === "newspaper") {
      // Legacy paid flow: open the Premium payment modal.
      setPaymentTarget(partner);
      return;
    }
    // All other types: open URL or Google Maps in a new tab + close carousel.
    let targetUrl = partner.url;
    const wantsMap = !!(partner.physical_address && partner.physical_address.trim()) &&
      (partner.type === "brand" || partner.type === "organization");
    if (wantsMap) {
      const q = (partner.google_maps_query && partner.google_maps_query.trim())
        || `${partner.name} ${partner.physical_address}`;
      targetUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
    }
    if (!targetUrl) {
      toast.error("Pas de lien disponible pour ce partenaire");
      return;
    }
    // Fire-and-forget click tracking.
    axios.post(`${API}/partners/${partner.id}/click`, { userId, conversationId: active?.id || null }).catch(() => {});
    window.open(targetUrl, "_blank", "noopener,noreferrer");
    setCarouselCategory(null);
  };

  const handleSend = useCallback(async (textArg) => {
    const text = (typeof textArg === "string" ? textArg : "").trim();
    if (!text || loading) return;
    const wantTtsAuto = voiceOutputEnabled && mode !== "casual";
    // v3.0.0-rc.8z-pwa — Signal PWA install (message envoyé = intent d'usage).
    try { require('@/pwa/valueSignals').trackSignal('chat_message_sent'); } catch (_) {}
    await sendMessageLive(text, wantTtsAuto ? { voice: true } : {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, voiceOutputEnabled, mode]);

  // v2.19.21e — Upload doc (trombone). MVP : 1 fichier, ≤5MB, PDF/TXT/MD/image.
  const uploadDoc = useCallback(async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      try { toast.error(`Fichier trop volumineux (${(file.size/1024/1024).toFixed(1)} Mo). Max 5 Mo.`); } catch (_) {}
      return;
    }
    setDocUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload-doc`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.ok) {
        setAttachedDoc({
          doc_id: res.data.doc_id,
          name: res.data.name,
          kind: res.data.kind,
          preview: res.data.preview || "",
          size: res.data.size,
        });
        toast.success(`📎 ${res.data.name} joint à la prochaine question.`);
      } else {
        toast.error(`Upload échoué : ${res.data?.error || "erreur inconnue"}`);
      }
    } catch (e) {
      const errMsg = e?.response?.data?.error || e?.message || "erreur réseau";
      toast.error(`Upload échoué : ${errMsg}`);
    } finally {
      setDocUploading(false);
    }
  }, []);

  // v2.19.24 — Upload + envoi immédiat en MODE ORDONNANCE (caméra arrière mobile).
  // Court-circuit du flux trombone : pas d'attachedDoc affiché, on envoie direct.
  const uploadOrdonnance = useCallback(async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      try { toast.error(`Image trop volumineuse (${(file.size/1024/1024).toFixed(1)} Mo). Max 5 Mo.`); } catch (_) {}
      return;
    }
    setDocUploading(true);
    let docId = null;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload-doc`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!res.data?.ok) {
        toast.error(`Upload échoué : ${res.data?.error || "erreur inconnue"}`);
        return;
      }
      docId = res.data.doc_id;
      toast.success("📋 Lecture de votre ordonnance en cours…");
    } catch (e) {
      const errMsg = e?.response?.data?.error || e?.message || "erreur réseau";
      toast.error(`Upload échoué : ${errMsg}`);
      return;
    } finally {
      setDocUploading(false);
    }
    if (!docId) return;
    // v2.19.27 — Envoi avec mode=magic : le backend classifie puis route vers
    // le bon flow (ordonnance / CNI / facture / analyse / texte libre).
    await sendMessageLive("Analyse ce document.", {
      modeOverride: "magic",
      docId,
    });
  }, [sendMessage]);

  // v1.20.1 — askMessage : envoyer un message custom sans passer par l'input.
  // Utilisé par les boutons des PersonalityCards (Biographie/Bilan/Héritage).
  const askMessage = async (text, extraOpts = {}) => {
    if (!text || loading) return;
    const wantTtsAuto = voiceOutputEnabled && mode !== "casual";
    // 2026-07-02 — extraOpts (selectedProductId/selectedMerchantId) : le choix
    // utilisateur des cartes DOIT traverser toute la chaîne jusqu'à sendAgentV3.
    await sendMessageLive(text, { ...(wantTtsAuto ? { voice: true } : {}), ...extraOpts });
  };

  // v2.19.2d — pushLocalDetail : pousse une bulle agent LOCALE (kind:system_quick_search)
  // avec les détails déjà disponibles dans `item`. Aucun fetch backend → zéro risque 404.
  // Utilisé par les boutons "En savoir plus" sur les cards (Divertissement, Justice, etc.).
  const pushLocalDetail = useCallback((item) => {
    if (!active?.id || !item) return;
    const lines = [];
    const title = item.nom || item.name || item.title || item.label || "Détails";
    lines.push(`📍 **${title}**`);
    if (item.category_label || item.specialite) lines.push(`🏷️ ${item.category_label || item.specialite}`);
    if (item.commune_name || item.commune) lines.push(`📌 ${item.commune_name || item.commune}`);
    if (item.address || item.adresse) lines.push(`🏠 ${item.address || item.adresse}`);
    if (item.code_civ_ai || item.code_civ) lines.push(`🆔 \`${item.code_civ_ai || item.code_civ}\``);
    if (item.telephone || item.phone) lines.push(`📞 ${item.telephone || item.phone}`);
    if (item.horaires || item.hours) lines.push(`🕒 ${item.horaires || item.hours}`);
    if (item.prix_moyen || item.price_range || item.prix) lines.push(`💰 ${item.prix_moyen || item.price_range || item.prix}`);
    if (item.langues) lines.push(`🗣️ ${Array.isArray(item.langues) ? item.langues.join(", ") : item.langues}`);
    if (item.platform_offer || item.offer) lines.push(`\n🎁 **Offre AYA** : ${item.platform_offer || item.offer}`);
    if (item.description) lines.push(`\n${item.description}`);
    const detailText = lines.join("\n");
    try {
      conv.appendMessage(active.id, {
        role: "agent",
        content: detailText,
        ts: Date.now(),
        kind: "system_quick_search",
        isLocal: true,
      });
    } catch (_) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id, conv]);

  // ---- Speech Recognition ----
  const makeRecognition = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return null;
    const rec = new SR();
    rec.lang = "fr-FR";
    rec.interimResults = true;
    rec.continuous = false;
    return rec;
  };

  const clearNoSpeechTimer = () => {
    if (noSpeechTimerRef.current) {
      clearTimeout(noSpeechTimerRef.current);
      noSpeechTimerRef.current = null;
    }
  };

  const startListening = async () => {
    // v2.19.4b — Demande explicite de permission micro AVANT SpeechRecognition.
    //   Sur Chrome Android, `SpeechRecognition.start()` seul ne déclenche pas
    //   toujours le prompt de permission. On force le prompt via getUserMedia,
    //   puis on libère immédiatement le stream (on n'utilisera que SpeechRecognition).
    try {
      console.log("[VoiceInput] requesting mic permission via getUserMedia");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      console.log("[VoiceInput] mic permission granted");
    } catch (err) {
      console.warn("[VoiceInput] getUserMedia failed:", err && err.name, err && err.message);
      if (err && (err.name === "NotAllowedError" || err.name === "PermissionDeniedError")) {
        toast.error("🎤 Permission micro refusée. Tape le cadenas dans la barre d'adresse → Autorisations → Microphone → Autoriser, puis recharge la page.");
        return;
      }
      if (err && err.name === "NotFoundError") {
        toast.error("🎤 Aucun micro détecté sur cet appareil.");
        return;
      }
      toast.error("🎤 Erreur micro : " + ((err && err.message) || (err && err.name) || "inconnue"));
      return;
    }
    const rec = makeRecognition();
    if (!rec) {
      try { console.warn("[VoiceInput] SpeechRecognition non supporté"); } catch (_) {}
      toast.error("🎤 Reconnaissance vocale non supportée. Utilise Chrome ou Edge.");
      continueLoopRef.current = false;
      setVoiceState(VS.IDLE);
      return;
    }
    try { console.log("[VoiceInput] start listening (fr-FR)"); } catch (_) {}
    lastFinalRef.current = "";
    setInterimTranscript("");
    setVoiceState(VS.LISTENING);

    // Auto-stop if no speech detected in 8s
    clearNoSpeechTimer();
    noSpeechTimerRef.current = setTimeout(() => {
      try { rec.stop(); } catch (_) {}
    }, 8000);

    rec.onresult = (event) => {
      let interim = "";
      let final = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) final += r[0].transcript;
        else interim += r[0].transcript;
      }
      if (final) lastFinalRef.current += final;
      if (interim || final) {
        // user actually said something → reset silence timer
        clearNoSpeechTimer();
      }
      setInterimTranscript(lastFinalRef.current + interim);
    };

    rec.onerror = (e) => {
      clearNoSpeechTimer();
      console.warn("[VoiceInput] error:", e.error);
      // v2.19.4a — Messages FR friendly par type d'erreur SpeechRecognition.
      if (e.error === "no-speech") {
        toast("🎤 Je n'ai rien entendu. Re-clique pour réessayer.");
      } else if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        toast.error("🚫 Permission micro refusée. Autorise AYA dans les paramètres de ton navigateur.");
      } else if (e.error === "audio-capture") {
        toast.error("🎤 Aucun micro détecté sur ton appareil.");
      } else if (e.error === "network") {
        toast.error("📡 Erreur réseau pendant la reconnaissance vocale. Réessaie.");
      } else if (e.error === "aborted") {
        // Silent — user-initiated stop.
      } else {
        toast.error(`🎤 Erreur reconnaissance vocale : ${e.error}`);
      }
      setInterimTranscript("");
      setVoiceState(VS.IDLE);
      continueLoopRef.current = false;
    };

    rec.onstart = () => {
      try { console.log("[VoiceInput] onstart fired"); } catch (_) {}
    };

    rec.onend = async () => {
      clearNoSpeechTimer();
      const finalText = lastFinalRef.current.trim();
      setInterimTranscript("");
      // Filter empty / noise / very short transcripts (Bug 1 — ghost LLM call from silence)
      if (!finalText || finalText.length < 3) {
        if (finalText) console.log("[voice] ignored_short_transcript", JSON.stringify(finalText));
        else console.log("[voice] ignored_empty_transcript");
        setVoiceState(VS.IDLE);
        continueLoopRef.current = false;
        return;
      }
      setVoiceState(VS.PROCESSING);
      await sendMessageLive(finalText, { voice: true, source: "voice" });
      // Audio queue handles playback in background (chunked TTS).
      // Wait until queue drained before optionally re-listening.
      while (audioPlayingRef.current || audioQueueRef.current.length > 0) {
        await new Promise((r) => setTimeout(r, 150));
      }
      if (continueLoopRef.current && continuousMode && !interruptedRef.current) {
        startListening();
      } else {
        setVoiceState(VS.IDLE);
      }
    };

    recognitionRef.current = rec;
    try {
      rec.start();
      try { console.log("[VoiceInput] rec.start() called"); } catch (_) {}
    } catch (err) {
      console.warn("[VoiceInput] rec.start error:", err);
      // v2.19.4a — Toast FR si SpeechRecognition refuse de démarrer (déjà actif, etc.).
      toast.error(`🎤 Démarrage micro impossible : ${err.message || "erreur navigateur"}`);
      setVoiceState(VS.IDLE);
      continueLoopRef.current = false;
    }
  };

  const stopSession = () => {
    continueLoopRef.current = false;
    clearNoSpeechTimer();
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (_) {}
    }
    stopAllAudio();
    setVoiceState(VS.IDLE);
    setInterimTranscript("");
  };

  const handleVoiceButton = () => {
    // v2.19.1 — debug log + fallback STT classic
    try {
      console.log("[VoiceInput] click — voiceState:", voiceState, "mode:", mode, "liveMode:", liveMode);
    } catch (_) {}
    // v2.19.1 — Le mode live (WebSocket Realtime) est désactivé par défaut pour
    // robustesse (iframe preview, permissions micro). On utilise TOUJOURS le
    // STT classique sauf si l'utilisateur a explicitement activé liveMode ET
    // qu'il est en mode casual.
    const useLiveSession = mode === "casual" && liveMode === true;
    if (useLiveSession) {
      if (liveSessionRef.current) {
        stopLiveSession();
      } else {
        startLiveSession();
      }
      return;
    }
    // ---- STT classique (SpeechRecognition fr-FR) ----
    if (streamingActive || audioActive) {
      interrupt();
      setTimeout(() => {
        continueLoopRef.current = continuousMode;
        startListening();
      }, 80);
      return;
    }
    if (voiceState === VS.LISTENING) {
      try { recognitionRef.current?.stop(); } catch (_) {}
      return;
    }
    if (voiceState === VS.SPEAKING) {
      stopAllAudio();
      if (continuousMode) startListening();
      else { setVoiceState(VS.IDLE); continueLoopRef.current = false; }
      return;
    }
    if (voiceState === VS.PROCESSING) return;
    continueLoopRef.current = continuousMode;
    startListening();
  };

  // ---- Mode change handler ----
  const handleModeChange = (newMode) => {
    if (newMode === mode) return;
    conv.create(newMode);
    toast(`Nouvelle discussion en mode ${newMode}`);
  };

  // ---- Render ----
  const messages = active?.messages || [];
  // v2.19.2b — "empty" = pas de user message (le greeting agent compte comme vide).
  const isEmpty = messages.length === 0 || (!messages.some((m) => m.role === "user") && messages.every((m) => m.isGreeting || m.role !== "user"));

  // v1.12.1 — Unified mic button. In casual + liveMode, the button reflects
  // the WebSocket live session state. Otherwise, it falls back to the legacy
  // STT/TTS state machine.
  const liveActive = mode === "casual" && liveMode && !!liveSessionRef.current;
  const liveLabel = {
    idle: "Appuie pour parler",
    listening: "Je t'écoute",
    speaking: "Je réponds",
    interrupted: "Coupé",
    reconnecting: "Reconnexion…",
    closed: "Appuie pour parler",
  }[liveState] || "Appuie pour parler";
  const liveIcon = {
    idle: "🎙️",
    listening: "🟢",
    speaking: "🟡",
    interrupted: "🔴",
    reconnecting: "🟠",
    closed: "🎙️",
  }[liveState] || "🎙️";

  const voiceLabel = liveActive ? liveLabel : {
    [VS.IDLE]: "Parler avec AYA",
    [VS.LISTENING]: "Écoute… (clic pour envoyer)",
    [VS.PROCESSING]: "Réflexion…",
    [VS.SPEAKING]: "Lecture… (clic pour couper)",
  }[voiceState];
  const voiceIcon = liveActive ? liveIcon : {
    [VS.IDLE]: "🎙️",
    [VS.LISTENING]: "🔴",
    [VS.PROCESSING]: "⏳",
    [VS.SPEAKING]: "🔊",
  }[voiceState];
  const voiceClassState = liveActive ? `live-${liveState}` : voiceState;
  const sessionActive = voiceState !== VS.IDLE;
  const liveBtnDisabled = !liveActive && voiceState === VS.PROCESSING;

  const bigVoiceButton = (
    <button
      type="button"
      className={`civ-voice-big civ-voice-${voiceClassState}`}
      onClick={handleVoiceButton}
      disabled={liveBtnDisabled}
      data-testid="voice-chat-button"
      aria-label={voiceLabel}
    >
      <span className="civ-voice-icon">{voiceIcon}</span>
      <span className="civ-voice-label">{voiceLabel}</span>
    </button>
  );

  const smallVoiceButton = (
    <button
      type="button"
      className={`civ-voice-small civ-voice-${voiceClassState}`}
      onClick={handleVoiceButton}
      disabled={liveBtnDisabled}
      data-testid="voice-chat-button"
      aria-label={voiceLabel}
      title={voiceLabel}
    >
      <span className="civ-voice-icon-sm">{voiceIcon}</span>
    </button>
  );

  return (
    <div className="civ-app" data-testid="civ-app">
      {/* v3.0.0-rc.7-phaseB-llm-toolcall-v1 — Phase B.3.1 : modale confirmation agent (intent=purchase) */}
      {pendingPurchase && (
        <PurchaseConfirmationModal
          open={true}
          merchant={{ id: pendingPurchase.merchant_id, name: pendingPurchase.merchant_name || pendingPurchase.merchant_id }}
          items={pendingPurchase.item_summary}
          amount_fcfa={pendingPurchase.amount_fcfa}
          walletLabel={typeof pendingPurchase.current_balance_fcfa === "number" ? `Wallet — ${new Intl.NumberFormat("fr-FR").format(pendingPurchase.current_balance_fcfa)} FCFA` : "Wallet Principal"}
          confirmUrl={`${API}/v3/agent/chat/confirm`}
          confirmBodyBuilder={() => ({
            userId,
            session_id: pendingPurchase.session_id,
            parent_entry_id: pendingPurchase.parent_entry_id,
            idempotency_key: pendingPurchase.idempotency_key,
          })}
          onClose={() => setPendingPurchase(null)}
          onSuccess={(result) => {
            try {
              if (active && result?.status === "APPROVED") {
                conv.appendMessage(active.id, {
                  role: "agent",
                  content: `✅ Commande confirmée — ton argent est sous séquestre jusqu'à la livraison.\n\n🔐 **Code de réception : ${result.pin || "—"}** — donne-le au livreur à l'arrivée (c'est lui qui déclenche le paiement).\n\nCommande \`${result.order_id || result.transaction_id || result.order_ref}\` · Nouveau solde : ${typeof result.new_balance_fcfa === "number" ? new Intl.NumberFormat("fr-FR").format(result.new_balance_fcfa) + " FCFA" : "—"}${result.dispatch_started ? "\n🛵 Recherche d'un livreur en cours…" : ""}`,
                  ts: Date.now(),
                  streaming: false,
                  agent_meta: { confirmed: true, child_entry_id: result.child_entry_id, tracking_order_id: result.order_id || null },
                });
              }
            } catch (_) {}
          }}
        />
      )}
      {/* 2026-07-02 — Address Gate (§27.5) : carte address_creation bloquante + reprise auto. */}
      {acGate && (
        <AddressCreationTemplate
          payload={acGate.payload}
          state={acGate.status}
          onSubmit={handleAcSubmit}
          onRequestGps={handleAcGps}
          onCancel={() => setAcGate(null)}
        />
      )}
      <Sidebar
        conversations={conv.list}
        activeId={conv.activeId}
        onSelect={conv.select}
        onCreate={() => conv.create(mode)}
        onDelete={conv.delete}
        onClearAll={conv.clearAll}
        manifest={manifest}
        mobileOpen={sidebarOpen}
        onCloseMobile={() => setSidebarOpen(false)}
        desktopCollapsed={desktopCollapsed}
        mode={mode}
        onModeChange={handleModeChange}
        voiceOutputEnabled={voiceOutputEnabled}
        onVoiceOutputToggle={setVoiceOutputEnabled}
        webSearchMode={webSearchMode}
        onWebSearchModeChange={setWebSearchMode}
        continuousMode={continuousMode}
        onContinuousModeToggle={setContinuousMode}
        responseLang={responseLang}
        onResponseLangChange={setResponseLang}
        theme={theme}
        onThemeToggle={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
        AuthControl={<HeaderAuthControl />}
      />

      <div className="civ-chat-root aya-prive-shell">
        {/* Fond jungle (maquette) — inline style car css-loader ne résout pas /public. */}
        <div className="aya-prive-bg" aria-hidden="true" style={{ backgroundImage: "url('/aya/prive/jungle-bg.jpg')" }} />
        {/* v3.0.0-rc.8 — Le masque AYA a migré dans le halo (LiveBanner header).
            Le floating notch n'est donc plus rendu. On garde le handler dispo pour
            passer à <LiveBanner onMenuClick=... />. */}
        {/* Badge wallet retiré du chat — déplacé vers /mon-compte (demande user, 2026-07-02). */}
        {sessionActive && (
          <button
            type="button"
            className="civ-stop-btn civ-stop-btn-floating"
            onClick={stopSession}
            data-testid="voice-stop-btn"
            aria-label="Arrêter la session vocale"
          >
            ⏹️ Arrêter
          </button>
        )}
        {/* HEADER — v2.19.23 : header complètement supprimé. */}

        {/* v1.12.1 — Yellow Voix Live panel removed. All live session UX is now
            consolidated on the main mic button + transient subtitles above the
            input bar. */}

        {/* v5.6.0 — CATÉGORIES MARKETPLACE SUPPRIMÉES DU CHAT.
            Les catégories (Commerces, Santé, Justice, Divertissement, Emploi,
            Admin, Finance, Géocodage…) appartiennent désormais à AYA SEARCH
            (/recherche). Le Chat reste une expérience conversationnelle pure :
            comprendre, répondre, expliquer, rechercher, guider — avec au besoin
            UNE carte contextuelle, jamais une marketplace. */}

        {/* v2.15.4 — Sous-onglets Live SUPPRIMÉS (Sport/Festival/Dégustation/etc.). */}

        {/* PARTNERS CAROUSEL — opens when user clicks a category chip with ≥1 active partner.
            v2.19.2 — restreint au mode "quotidien" pour ne pas polluer les modes thématiques
            (Justice, Santé, Emploi, etc.) avec le bloc "📰 Choisir un journal". */}
        {carouselCategory && themeMode === "quotidien" && (
          <PartnersCarousel
            category={carouselCategory}
            partners={partnersByCategory(carouselCategory)}
            onClose={() => setCarouselCategory(null)}
            onLeft={(np) => handlePartnerLeftClick(np)}
            onRight={(np) => handlePartnerRightClick(np)}
          />
        )}

        {/* v1.14.0 — Fullscreen voice hero removed; mic button is integrated
            inline in the footer next to the send button. */}

        {/* MESSAGES */}
        <main className="civ-messages" ref={scrollRef} data-testid="messages-container">
          {/* v2.19.2 — welcome bubble hardcoded "Akwaba" SUPPRIMÉE.
              Le greeting dynamique du themeMode (via /api/theme-modes/greetings)
              est désormais auto-injecté dans la conversation au mount par le hook
              _greetingBootedSetRef. */}
          {/* v2.19.2c — FeaturedCards en sous-onglet RETIRÉ.
              Les clicks sur les cards de catégorie (Quotidien et autres modes) poussent
              désormais une bulle agent inline avec rag_media `featured_items_grid` /
              `justice_grid` / `health_grid` / etc. directement dans la conversation.
              Voir onCategoryClick / onJusticeCategoryClick / etc. */}
          {/* eslint-disable-next-line no-constant-binary-expression */}
          {false && isEmpty && themeMode === "quotidien" && activeCategory && (
            activeCategory === "live" ? (
              <FeaturedCards
                items={liveEvents}
                categoryImage={getCategoryImage("live") || {}}
                emptyHint="Aucun event live pour le moment."
                testIdPrefix="live"
                showPrice
                onItemClick={(ev) => {
                  if (ev.address_civ) navigate(`/event/${encodeURIComponent(ev.address_civ)}`);
                }}
                onLearnMore={(ev) => sendMessage(`Donne-moi plus de détails sur l'événement "${ev.title}".`)}
                extraActions={(ev) => {
                  const isFree = !ev.ticket_price_fcfa || ev.ticket_price_fcfa === 0;
                  return (
                    <button
                      type="button"
                      onClick={() => { if (ev.address_civ) navigate(`/event/${encodeURIComponent(ev.address_civ)}`); }}
                      data-testid={`live-reserve-${ev.id}`}
                      style={{
                        background: isFree ? "#16A34A" : "#F59E0B",
                        color: isFree ? "#fff" : "#0f172a",
                        border: "none", padding: "5px 12px", borderRadius: 8,
                        fontSize: 11.5, fontWeight: 700, cursor: "pointer",
                      }}
                    >{isFree ? "✅ Confirmer présence" : "🎟 Réserver"}</button>
                  );
                }}
              />
            ) : (
              <FeaturedCards
                items={getFeaturedItems(activeCategory)}
                categoryImage={getCategoryImage(activeCategory) || {}}
                emptyHint={`Aucun contenu featured pour ${activeCategory}.`}
                testIdPrefix={`featured-${activeCategory}`}
                onItemClick={(it) => {
                  const prompt = `Parle-moi de "${it.title}" — ${it.subtitle || activeCategory} en Côte d'Ivoire.`;
                  sendMessage(prompt);
                }}
                onLearnMore={(it) => {
                  if (activeCategory === "news") sendMessage(`Approfondis cette actualité : ${it.title}`);
                  else sendMessage(`Parle-moi plus de "${it.title}" — ${activeCategory} en Côte d'Ivoire.`);
                }}
                extraActions={activeCategory === "news" ? (it) => (
                  <button
                    type="button"
                    data-testid="news-synthesis-btn"
                    onClick={async () => {
                      try {
                        toast.message("💳 Paiement en cours…");
                        const r = await axios.post(`${API}/payments/initiate`, {
                          user_id: userId,
                          amount_fcfa: 100,
                          label: `Synthèse - ${it.title}`,
                          address_civ: "",
                          item_type: "news_synthesis",
                          item_id: it.id,
                        });
                        const payUrl = r.data?.payment_url || "";
                        if (payUrl.includes("#mock-cinetpay") || r.data?.mock) {
                          toast.success("✅ Synthèse débloquée");
                          sendMessage(`Fais-moi une synthèse complète de l'actualité "${it.title}" en 5 points`);
                        } else if (payUrl) {
                          window.open(payUrl, "_blank");
                        } else {
                          toast.error("Paiement indisponible pour l'instant");
                        }
                      } catch (e) {
                        toast.error("Erreur paiement : " + (e.response?.data?.error || e.message));
                      }
                    }}
                    style={{
                      background: "linear-gradient(135deg,#F59E0B,#FBBF24)",
                      color: "#0F172A", border: "none", padding: "5px 12px",
                      borderRadius: 8, fontSize: 11.5, fontWeight: 700, cursor: "pointer",
                    }}
                  >📖 Synthèse 100 FCFA</button>
                ) : null}
              />
            )
          )}
          {/* 2026-06 — L'ancienne HomeCard (Bonjour + météo/EUR-XOF/wallet) est SUPPRIMÉE
              DÉFINITIVEMENT : remplacée par AyaPriveHero conforme à la maquette premium. */}
          {/* v4.7.0 — Kiosque déplacé DANS la HomeCard (sous les mini-cartes). */}
          {/* v3.0.0-rc.8g — Strip "Nouveautés chez tes favoris" (chat home).
              Ne s'affiche que si l'utilisateur a des favoris ET qu'il n'y a
              qu'un greeting (≤ 1 message = début de conversation). */}
          {/* 2026-06 — Accueil AYA Privé : reproduction fidèle de la maquette
              « Interface premium AYA » (header, bloc accueil, indicateurs, zone vocale). */}
          {isEmpty && (
            <AyaPriveHero
              userName={(authUser?.first_name || authUser?.name || "Invité").split(" ")[0]}
              onTalk={handleVoiceButton}
              voiceState={liveActive ? liveState : voiceState}
              onMenu={() => {
                if (typeof window !== "undefined" && window.innerWidth >= 1024) {
                  setDesktopCollapsed((v) => !v);
                } else {
                  setSidebarOpen((v) => !v);
                }
              }}
              onScrollToInput={() => {
                const el = document.querySelector('.civ-composer textarea, .civ-composer input[type="text"], textarea');
                el?.focus();
              }}
            />
          )}
          {/* 2026-06 — Conversation : le masque RESTE visible (version réduite sticky). */}
          {!isEmpty && (
            <AyaPriveMiniMask
              listening={(liveActive ? liveState : voiceState) === "listening"}
              onMenu={() => {
                if (typeof window !== "undefined" && window.innerWidth >= 1024) {
                  setDesktopCollapsed((v) => !v);
                } else {
                  setSidebarOpen((v) => !v);
                }
              }}
            />
          )}
          {!isEmpty && messages.length <= 1 && (
            <FavoritesNoveltiesStrip onSelectProduct={(p) => sendMessage(`Parle-moi de ${p.name}`)} />
          )}
          {messages.map((m, i) => {
            // 2026-06 — L'ancienne carte greeting est SUPPRIMÉE : accueil = AyaPriveHero.
            if (m.isGreeting) return null;
            // v1.20.2 — userQuery = dernier message utilisateur avant cette bulle agent.
            // Utilisé par PersonalityCardBlock pour décider si une carte est pertinente.
            let userQuery = "";
            for (let j = i - 1; j >= 0; j--) {
              if (messages[j]?.role === "user") { userQuery = messages[j].content || ""; break; }
            }
            // v2.19.20b — Hide empty streaming agent stub. Le `<SophisticatedLoader>`
            // couvre déjà la phase listening/rag/tools/streaming. Afficher en plus une
            // bulle agent VIDE crée un trou visuel après le user msg (perçu comme
            // "retour à l'accueil" sur le 1er envoi). Dès que du contenu arrive
            // (content, webSearch, ui_components, sponsored, rag_media), la bulle
            // s'affiche normalement.
            const isEmptyAgentStub =
              m.role === "agent" &&
              m.streaming &&
              !(m.content && m.content.length > 0) &&
              !m.webSearch &&
              !m.ui_components &&
              !m.sponsored &&
              !m.rag_media &&
              !m.sponsored_cards;
            if (isEmptyAgentStub) return null;
            // v2.19.1 — Show suggested chips only on greeting bubble AND only if
            // no user message has been posted yet in this conversation.
            // v2.19.29c — Réactivé dans la bulle greeting (centre chat), limité à 3 chips.
            const showChips = m.role === "agent"
              && Array.isArray(m.suggestedQuestions)
              && m.suggestedQuestions.length > 0
              && !messages.some((x, k) => k > i && x.role === "user")
              && !messages.slice(0, i).some((x) => x.role === "user");
            return (
              <div key={i}>
                <MessageBubble msg={m} index={i} onAction={handleMessageAction} apiBase={API} userId={userId} conversationId={active?.id} mode={mode} ask={askMessage} userQuery={userQuery} pushLocalDetail={pushLocalDetail} />
                {showChips && (
                  <SuggestedQuestionsChips
                    questions={m.suggestedQuestions.slice(0, 3)}
                    accent={m.modeAccent || "#FF7E1B"}
                    disabled={loading}
                    onPick={(q) => { askMessage(q); }}
                    testId={`suggested-chips-${i}`}
                  />
                )}
              </div>
            );
          })}
          {voiceState === VS.LISTENING && interimTranscript && (
            <div
              className="civ-bubble civ-bubble-user civ-bubble-interim"
              data-testid="interim-transcript"
            >
              <div className="civ-bubble-text">🎤 {interimTranscript}</div>
            </div>
          )}
          {/* v2.19.20a — Loader reste monté tant que loading===true ou voice processing,
              indépendamment de la phase (streaming inclus). Évite le "trou" entre l'ouverture
              du stream et la fin du post-process. La phase est mise à jour dynamiquement
              (listening → rag_fetching → tools_calling → streaming → post_processing). */}
          {(loading || voiceState === VS.PROCESSING) && (
            <SophisticatedLoader
              mode={themeMode || mode}
              phase={loadingPhase}
              modeLabel={themeModesGreetings[themeMode]?.label || ""}
            />
          )}
        </main>

        {/* v3.0.0-rc.8 — LiveBanner devient un halo utilitaire PERMANENT (menu masque + panier + mon compte).
            Plus de fetch campagne pub. Coins haut carrés, bas arrondis. */}
        {/* 2026-06 — AYA Privé : le pad garde TOUJOURS le style maquette (minimal),
            même après une réponse LLM (plus de retour à "l'ancienne version"). */}
        <LiveBanner
          thinking={streamingActive}
          minimal={true}
          onMenuClick={() => {
            if (typeof window !== "undefined" && window.innerWidth >= 1024) {
              setDesktopCollapsed((v) => !v);
            } else {
              setSidebarOpen((v) => !v);
            }
          }}
        >
        {/* INPUT BAR */}
        <footer className="civ-input-bar" data-testid="input-bar">
          {/* v1.12.1 — Live transcripts as transient subtitles + mute pill */}
          {liveActive && (liveTranscriptUser || liveTranscriptAgent || liveError) && (
            <div className="civ-live-subtitles" data-testid="live-subtitles" style={{
              position: "absolute",
              bottom: "100%",
              left: 12,
              right: 12,
              marginBottom: 6,
              padding: "8px 12px",
              background: "rgba(15, 23, 42, 0.92)",
              color: "#f8fafc",
              borderRadius: 10,
              fontSize: 12.5,
              lineHeight: 1.45,
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
              maxHeight: 110,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 3,
              pointerEvents: "none",
            }}>
              {liveTranscriptUser && (
                <div data-testid="live-transcript-user" style={{ opacity: 0.85 }}>
                  <span style={{ opacity: 0.6, marginRight: 6 }}>vous</span>
                  <em>{liveTranscriptUser}</em>
                </div>
              )}
              {liveTranscriptAgent && (
                <div data-testid="live-transcript-agent">
                  <span style={{ opacity: 0.6, marginRight: 6 }}>AYA</span>
                  {liveTranscriptAgent}
                </div>
              )}
              {liveError && (
                <div data-testid="live-error" style={{ color: "#fca5a5" }}>⚠️ {liveError}</div>
              )}
            </div>
          )}
          {liveActive && (
            <button
              type="button"
              onClick={toggleLiveMute}
              data-testid="live-mute-pill"
              title={liveMuted ? "Réactiver le micro" : "Couper le micro"}
              style={{
                padding: "6px 10px",
                background: liveMuted ? "#f59e0b" : "#fff",
                color: liveMuted ? "#fff" : "#334155",
                border: "1px solid #cbd5e1",
                borderRadius: 9999,
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {liveMuted ? "🔇" : "🎤"}
            </button>
          )}
          {(streamingActive || audioActive) && (
            <button
              type="button"
              className="civ-interrupt-btn"
              onClick={interrupt}
              data-testid="interrupt-btn"
              title="Interrompre la réponse en cours"
            >
              ⏹️ Interrompre
            </button>
          )}
          <InputBar
            ref={inputBarRef}
            minimal={true}
            loading={loading}
            onSend={handleSend}
            mode={mode}
            setMode={setMode}
            voiceOutputEnabled={voiceOutputEnabled}
            setVoiceOutputEnabled={setVoiceOutputEnabled}
            theme={theme}
            setTheme={setTheme}
            rapideMode={rapideMode}
            setRapideMode={setRapideMode}
            themeMode={themeMode}
            setThemeMode={setThemeMode}
            smallVoiceButton={smallVoiceButton}
            attachedDoc={attachedDoc}
            onPickFile={uploadDoc}
            onPickOrdonnance={uploadOrdonnance}
            onRemoveAttachedDoc={() => setAttachedDoc(null)}
            docUploading={docUploading}
          />
        </footer>
        </LiveBanner>
      </div>

      {/* PAYMENT MODAL — appears when user clicks "💰 Résumé complet (100 FCFA)" on a newspaper */}
      {paymentTarget && (
        <PaymentModal
          newspaper={paymentTarget}
          userId={userId}
          conversationId={active?.id}
          apiBase={API}
          onClose={() => setPaymentTarget(null)}
          onSuccess={(np, tx) => {
            setPaymentTarget(null);
            setCarouselCategory(null);
            const text = buildPremiumPrompt(np);
            sendMessage(text, {
              include_domains: [np.domain],
              force_web: "on",
              premium: { newspaper: np.id, partner_name: np.name, partner_domain: np.domain, category: np.category, tx_id: tx.id, amount_fcfa: tx.amount_fcfa, paid_at: tx.paid_at },
            });
          }}
        />
      )}

      {/* v2.19.1 — Confirm modal pour switch de thème mode quand discussion en cours. */}
      {pendingModeSwitch && (
        <div
          className="civ-modal-backdrop"
          data-testid="mode-switch-confirm-backdrop"
          onClick={() => setPendingModeSwitch(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 9999, padding: 16,
          }}
        >
          <div
            data-testid="mode-switch-confirm-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#FFFFFF", color: "#0F172A",
              borderRadius: 16, padding: 22, maxWidth: 380, width: "100%",
              boxShadow: "0 24px 48px rgba(0,0,0,0.35)",
              border: "1px solid #E2E8F0",
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
              {(themeModesGreetings[pendingModeSwitch.to]?.icon) || "🎭"}{" "}
              Nouvelle discussion en mode{" "}
              <span style={{ color: themeModesGreetings[pendingModeSwitch.to]?.accent || "#FF7E1B" }}>
                {themeModesGreetings[pendingModeSwitch.to]?.label || pendingModeSwitch.to}
              </span>{" "}?
            </div>
            <div style={{ fontSize: 13.5, color: "#475569", lineHeight: 1.5, marginBottom: 18 }}>
              Ta discussion actuelle sera archivée. Une nouvelle conversation va démarrer avec un guide adapté.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                type="button"
                data-testid="mode-switch-cancel-btn"
                onClick={() => {
                  // Restaure l'ancien mode (annule le switch).
                  const prev = pendingModeSwitch.from;
                  setPendingModeSwitch(null);
                  // Restaure sans re-déclencher le hook (utilise le ref).
                  _prevThemeModeRef.current = prev;
                  setThemeMode(prev);
                }}
                style={{
                  padding: "9px 16px", borderRadius: 10, border: "1.5px solid #CBD5E1",
                  background: "#FFFFFF", color: "#0F172A", fontWeight: 600, cursor: "pointer",
                }}
              >Annuler</button>
              <button
                type="button"
                data-testid="mode-switch-confirm-btn"
                onClick={() => {
                  const to = pendingModeSwitch.to;
                  setPendingModeSwitch(null);
                  applyThemeModeTransition(to);
                }}
                style={{
                  padding: "9px 16px", borderRadius: 10, border: "none",
                  background: themeModesGreetings[pendingModeSwitch.to]?.accent || "#FF7E1B",
                  color: "#FFFFFF", fontWeight: 700, cursor: "pointer",
                }}
              >Démarrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MarkdownContent({ text, streaming, sources }) {
  // 2026-07-02 — Anti "trous" visuels : compresse 3+ sauts de ligne consécutifs en 2.
  // Le LLM viole parfois la règle max-2-newlines du prompt ; on garantit le rendu ici.
  const compact = (text || "").replace(/\n[ \t]*\n[ \t]*\n+/g, "\n\n");
  const linkified = sources && sources.length ? linkifyCitations(compact, sources) : compact;
  return (
    <div className={`civ-md ${streaming ? "civ-md-streaming" : ""}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={{
          a: ({ node, href, children, ...props }) => {
            const txt = (Array.isArray(children) ? children.join("") : String(children || "")).trim();
            const isCit = /^\[\d{1,2}\]$/.test(txt);
            return (
              <a
                {...props}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={isCit ? "civ-citation" : undefined}
              >
                {children}
              </a>
            );
          },
          code: ({ node, inline, className, children, ...props }) =>
            inline ? (
              <code className="civ-md-code-inline" {...props}>{children}</code>
            ) : (
              <pre className="civ-md-code-block"><code {...props}>{children}</code></pre>
            ),
          ul: ({ node, ...props }) => <ul className="civ-md-list" {...props} />,
          ol: ({ node, ...props }) => <ol className="civ-md-list" {...props} />,
        }}
      >
        {linkified}
      </ReactMarkdown>
      {streaming && <span className="civ-stream-cursor" aria-hidden="true">▍</span>}
    </div>
  );
}

function WebSearchSources({ webSearch }) {
  const [open, setOpen] = useState(true);
  if (!webSearch) return null;
  if (webSearch.error) {
    return (
      <div className="civ-web-error" data-testid="web-search-error">
        ⚠️ Recherche web indisponible : <code>{webSearch.error}</code>
      </div>
    );
  }
  const sources = webSearch.sources || [];
  if (sources.length === 0) return null;
  const cacheTag = webSearch.cache_hit ? " · cache" : "";
  const dur = webSearch.duration_ms ? ` · ${webSearch.duration_ms}ms` : "";
  return (
    <div className="civ-sources" data-testid="web-search-sources">
      <button
        type="button"
        className="civ-sources-toggle"
        onClick={() => setOpen((v) => !v)}
        data-testid="web-search-sources-toggle"
      >
        📚 Sources ({sources.length}){cacheTag}{dur} {open ? "▾" : "▸"}
      </button>
      {open && (
        <ol className="civ-sources-list">
          {sources.map((s, i) => (
            <li key={i} className="civ-source-item">
              <span className="civ-source-num">[{i + 1}]</span>
              <a href={s.url} target="_blank" rel="noopener noreferrer" className="civ-source-link">
                {s.title || s.url}
              </a>
              {s.published && <span className="civ-source-date"> · {String(s.published).slice(0, 10)}</span>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function MessageActions({ message, index, onAction, mode, userId }) {
  if (message.streaming) return null;
  const showTtsButton = mode === "casual" && message.role === "agent" && !!message.content;
  // v2.19.39-sec11 — Icônes lucide monochromes uniformes (20px). Plus aucun emoji.
  const ICON = { width: 18, height: 18, strokeWidth: 1.75 };
  return (
    <div className="civ-msg-actions" data-testid={`msg-actions-${index}`}>
      <button className="civ-msg-action-btn" title="Copier" onClick={() => onAction("copy", message, index)} data-testid={`msg-copy-${index}`}><IconCopy {...ICON} /></button>
      <button
        className={`civ-msg-action-btn ${message.liked ? "active liked" : ""}`}
        title="J'aime"
        onClick={() => onAction("like", message, index)}
        data-testid={`msg-like-${index}`}
      ><IconThumbsUp {...ICON} /></button>
      <button
        className={`civ-msg-action-btn ${message.disliked ? "active disliked" : ""}`}
        title="Je n'aime pas"
        onClick={() => onAction("dislike", message, index)}
        data-testid={`msg-dislike-${index}`}
      ><IconThumbsDown {...ICON} /></button>
      <button className="civ-msg-action-btn" title="Régénérer" onClick={() => onAction("regenerate", message, index)} data-testid={`msg-regen-${index}`}><IconRefresh {...ICON} /></button>
      <button className="civ-msg-action-btn" title="Partager" onClick={() => onAction("share", message, index)}><IconShare {...ICON} /></button>
      <button className="civ-msg-action-btn" title="Plus" onClick={() => onAction("more", message, index)}><IconMore {...ICON} /></button>
      {showTtsButton && (
        <TTSPlayButton text={message.content} mode={mode} userId={userId} />
      )}
      {message.regenCount > 0 && (
        <span className="civ-msg-regen-count" title="Nombre de régénérations">↻ {message.regenCount}</span>
      )}
    </div>
  );
}

// v2.19.29c — GreetingText : render custom pour bulle isGreeting.
// Split sur 🇨🇮 → insère <FlagCi /> SVG inline (garantit le rendu sur tout OS).
// Parse aussi **bold**  light, suffisant pour les greetings).
function GreetingText({ text }) {
  if (!text) return null;
  const flagParts = String(text).split("🇨🇮");
  const out = [];
  flagParts.forEach((segment, fi) => {
    const boldParts = segment.split(/(\*\*[^*]+\*\*)/g);
    boldParts.forEach((bp, bi) => {
      if (!bp) return;
      const mBold = bp.match(/^\*\*([^*]+)\*\*$/);
      if (mBold) {
        out.push(<strong key={`g-${fi}-b-${bi}`}>{mBold[1]}</strong>);
      } else {
        out.push(<span key={`g-${fi}-t-${bi}`}>{bp}</span>);
      }
    });
    if (fi < flagParts.length - 1) out.push(<FlagCi key={`g-${fi}-flag`} size={16} />);
  });
  return <>{out}</>;
}

function MessageBubble({ msg, index, onAction, apiBase, userId, conversationId, mode, ask, userQuery, pushLocalDetail }) {
  const role = msg.role;
  const cls =
    role === "user" ? "civ-bubble civ-bubble-user"
    : role === "blocked" ? "civ-bubble civ-bubble-blocked"
    : role === "error" ? "civ-bubble civ-bubble-error"
    : "civ-bubble civ-bubble-agent aya-bubble-glass";
  const isAgent = role === "agent";
  const sources = msg.webSearch?.sources || null;
  const searching = isAgent && msg.streaming && !msg.content && msg.webSearch && !msg.webSearch.error && (msg.webSearch.sources || []).length === 0 && msg.webSearch.triggered;
  const [showOriginal, setShowOriginal] = useState(false);
  const [refundState, setRefundState] = useState(msg.premium?.refunded ? "refunded" : "idle"); // idle | requesting | refunded | error
  const tr = msg.translation;
  // When voice mode is on for this bubble, strip markdown + expand currency on
  // EACH chunk during streaming so the displayed text matches what ElevenLabs
  // is speaking (and so the user does not see "**5000 FCFA**" while hearing
  // "cinq mille francs CFA"). Numbers/punctuation/dates remain as-is to keep
  // the on-screen text readable.
  const rawDisplay = (tr && tr.original_fr && showOriginal) ? tr.original_fr : msg.content;
  // v2.19.20b — Strip robuste appliqué à CHAQUE rendu (donc à chaque token reçu),
  // pas seulement au chunk final. Cache aussi les markers PARTIELS pendant le
  // streaming (ex: "[[", "[[P", "[[PROFILE_CAR…") pour éviter le flash amateur
  // où l'utilisateur voit le tag se construire lettre par lettre avant que le
  // parser de cards ne le consomme. msg.content reste INTACT pour PersonalityCardBlock.
  const _CARD_KINDS = "PROFILE|COMMERCE|AVOCAT|MEDECIN|EVENT|PLACE";
  const _stripCardMarkers = (s) => {
    if (!s) return "";
    let out = s;
    // 1) Tags complets [[KIND_CARD: ...]]
    out = out.replace(new RegExp(`\\[\\[(?:${_CARD_KINDS})_CARD:[^\\]]*\\]\\]\\s*`, "gi"), "");
    // 2) Tag ouvert sans fermeture (en fin de stream): [[KIND_CARD: ...
    out = out.replace(new RegExp(`\\[\\[(?:${_CARD_KINDS})_CARD:[^\\]]*$`, "i"), "");
    // 3) Préfixe partiel en fin de buffer: "[", "[[", "[[P", "[[PROFILE_CAR"…
    out = out.replace(/\[\[?[A-Z_]{0,20}$/i, "");
    return out;
  };
  const _cleanRaw = _stripCardMarkers(rawDisplay);
  const displayContent = msg.voice ? normalizeForDisplay(_cleanRaw || "") : _cleanRaw;

  // Premium-summary "empty result" detection — fires only on agent + msg.premium messages
  // when streaming is done. Triggers refund banner if zero `[N]` citations or no Tavily sources.
  const premiumLooksEmpty = (
    isAgent && !msg.streaming && msg.premium && refundState !== "refunded"
    && (
      !/\[\d{1,2}\]/.test(msg.content || "")
      || (msg.webSearch && Array.isArray(msg.webSearch.sources) && msg.webSearch.sources.length === 0)
    )
  );

  const requestRefund = async () => {
    if (!msg.premium?.tx_id) return;
    setRefundState("requesting");
    try {
      await axios.post(`${apiBase}/payment/${msg.premium.tx_id}/refund`, { reason: "empty_summary" });
      setRefundState("refunded");
      toast.success("Remboursement effectué (mock)");
      onAction("refund_done", { ...msg, premium: { ...msg.premium, refunded: true } }, index);
    } catch (e) {
      setRefundState("error");
      toast.error(e.response?.data?.error || "Remboursement impossible");
    }
  };

  return (
    <div className={`${cls}${msg.premium ? " civ-bubble-premium" : ""}`} data-testid={`message-bubble-${role}-${index}`}>
      {isAgent && msg.premium && (
        <div className="civ-premium-badge" data-testid="premium-badge">
          ⭐ Résumé Premium · {msg.premium.amount_fcfa || 100} FCFA · {msg.premium.partner_name || msg.premium.newspaper}
          {refundState === "refunded" && <span style={{ marginLeft: 8, color: "#10b981" }}>· 💸 Remboursé</span>}
        </div>
      )}
      {isAgent && msg.premium && (msg.premium.partner_name || msg.premium.partner_domain) && (
        <div className="civ-premium-header" data-testid="premium-header">
          <div className="civ-premium-header-row">
            <span>📅 {new Date(msg.premium.paid_at || msg.ts || Date.now()).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
            <span>🕐 {new Date(msg.premium.paid_at || msg.ts || Date.now()).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: "Africa/Abidjan" })} (Abidjan)</span>
          </div>
          <div className="civ-premium-header-row">
            {msg.premium.partner_name && <span>📰 {msg.premium.partner_name}</span>}
            {msg.premium.partner_domain && <span>🌐 <code>{msg.premium.partner_domain}</code></span>}
          </div>
        </div>
      )}
      {premiumLooksEmpty && (
        <div className="civ-premium-empty-warn" data-testid="premium-empty-warn" style={{
          background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d",
          padding: "10px 12px", borderRadius: 8, margin: "6px 0 8px",
          fontSize: 13, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        }}>
          <span>⚠️ Sources limitées aujourd'hui pour ce partenaire.</span>
          <button
            type="button"
            onClick={() => onAction("regenerate", msg, index)}
            data-testid="premium-retry-btn"
            style={{ padding: "4px 10px", background: "#fff", border: "1px solid #fcd34d", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
          >🔄 Réessayer</button>
          <button
            type="button"
            onClick={requestRefund}
            disabled={refundState === "requesting" || !msg.premium?.tx_id}
            data-testid="premium-refund-btn"
            style={{ padding: "4px 10px", background: "#fff", border: "1px solid #fcd34d", borderRadius: 6, cursor: "pointer", fontWeight: 600, color: "#92400e" }}
          >{refundState === "requesting" ? "…" : "💸 Demander remboursement"}</button>
        </div>
      )}
      {isAgent && tr && tr.target_lang && !tr.error && (
        <div className="civ-translation-badge" data-testid="translation-badge">
          🌍 Traduit en <strong>{LANG_LABEL_SHORT[tr.target_lang] || tr.target_lang}</strong>
          {tr.cache_hit && <span> · cache ✓</span>}
          {tr.nllb_latency_ms && !tr.cache_hit && <span> · {tr.nllb_latency_ms}ms</span>}
        </div>
      )}
      {isAgent && tr?.error && (
        <div className="civ-translation-error" data-testid="translation-error">
          ⚠️ Traduction indisponible : <code>{tr.error}</code>
          {tr.estimated_time ? ` (${tr.estimated_time}s)` : null}
        </div>
      )}
      {isAgent && msg.webSearch?.triggered && (
        <div className="civ-web-pill" data-testid="web-search-pill">
          🌐 {msg.webSearch.cache_hit ? "Recherche web (cache)" : "Recherche web temps réel"}
          {msg.webSearch.sources?.length ? ` · ${msg.webSearch.sources.length} sources` : "…"}
        </div>
      )}
      {/* v1.20.1 — PersonalityCards AU-DESSUS du markdown (ordre inversé).
          v1.20.2 — Activé UNIQUEMENT si l'utilisateur a explicitement demandé
          une perso (detectPersonalityIntent côté composant). Plus de faux positifs. */}
      {isAgent && !msg.streaming && (
        <PersonalityCardBlock text={msg.content || ""} ask={ask} userQuery={userQuery} />
      )}
      <div className="civ-bubble-text">
        {isAgent ? (
          searching ? (
            <div className="civ-web-searching" data-testid="web-search-progress">
              🔍 Recherche web en cours…
            </div>
          ) : (msg.streaming && !msg.content) ? (
            /* [H_UIPOLISH_V1_L3 sous-palier 2.C] TypingDots inline (CSS pure motion.css) — pas d'import ajouté pour préserver MD5 LOCK range. */
            <span data-testid="agent-typing-dots" role="status" aria-live="polite" aria-label="L'assistant écrit" style={{ display: "inline-flex", alignItems: "center", gap: 4, verticalAlign: "middle", lineHeight: 0 }}>
              <span aria-hidden="true" style={{ display: "inline-block", width: 6, height: 6, borderRadius: "var(--aya-radius-full)", backgroundColor: "var(--aya-text-muted, currentColor)", opacity: 0.5, animation: "aya-anim-dots 1.2s var(--aya-ease-in-out, ease-in-out) 0ms infinite both" }} />
              <span aria-hidden="true" style={{ display: "inline-block", width: 6, height: 6, borderRadius: "var(--aya-radius-full)", backgroundColor: "var(--aya-text-muted, currentColor)", opacity: 0.5, animation: "aya-anim-dots 1.2s var(--aya-ease-in-out, ease-in-out) 160ms infinite both" }} />
              <span aria-hidden="true" style={{ display: "inline-block", width: 6, height: 6, borderRadius: "var(--aya-radius-full)", backgroundColor: "var(--aya-text-muted, currentColor)", opacity: 0.5, animation: "aya-anim-dots 1.2s var(--aya-ease-in-out, ease-in-out) 320ms infinite both" }} />
            </span>
          ) : msg.isGreeting ? (
            // v2.19.29c — Bulle greeting : rendu custom avec FlagCi SVG inline
            // (garantit le drapeau 🇨🇮 sur tous OS) + parsing simple du **gras**.
            <GreetingText text={displayContent || ""} />
          ) : (
            <MarkdownContent text={displayContent} streaming={msg.streaming} sources={sources} />
          )
        ) : (
          <>
            {/* v2.19.4a — Bulles "blocked" reçoivent désormais le rendu Markdown
                (le details FR friendly du backend contient du **gras** + listes). */}
            {role === "blocked" ? (
              <MarkdownContent text={msg.content || ""} streaming={false} sources={null} />
            ) : (
              <>{msg.content}</>
            )}
            {msg.streaming && <span className="civ-stream-cursor" aria-hidden="true">▍</span>}
          </>
        )}
      </div>
      {/* v3.0.0-rc.7-phaseCD — Phase D.2 : rich response (merchant_card) post markdown. */}
      {isAgent && !msg.streaming && msg.notif_kind === "delivered" && msg.delivery_meta?.order_ref && (
        <CourierRatingCard
          orderRef={msg.delivery_meta.order_ref}
          courierName={msg.delivery_meta.courier_name}
          apiBase={apiBase}
        />
      )}
      {isAgent && !msg.streaming && msg.agent_meta?.response_type === "rich" && Array.isArray(msg.agent_meta?.ui_blocks) && (
        <RichResponseRenderer
          ui_blocks={msg.agent_meta.ui_blocks}
          onQuickPrompt={(prompt) => ask && ask(prompt)}
          onMerchantSelect={(action, block) => ask && ask(
            `Commande chez ${action?.params?.merchant_name || block?.name || "ce commerce"}${block?.location ? ` (${block.location})` : ""} — ${action?.params?.price_fcfa || 0} FCFA`,
            { selectedMerchantId: action?.params?.merchant_id || block?.id || null }
          )}
          onProductAction={(action, block) => {
            if (action?.action_id === "view_shop") {
              // ⚪ Voir la boutique / la fiche → mini-site marchand complet.
              window.location.href = `/commercant/${action?.params?.merchant_id || block?.merchant_id}`;
              return;
            }
            if (action?.action_id === "contact_merchant") {
              // 📞 Immobilier / véhicule : contact direct WhatsApp du vendeur.
              const ph = String(action?.params?.merchant_phone || block?.merchant_phone || "").replace(/[^\d]/g, "");
              const txt = encodeURIComponent(`Bonjour, je vous contacte via AYA au sujet de : ${action?.params?.product_name || block?.name}`);
              if (ph) window.open(`https://wa.me/${ph}?text=${txt}`, "_blank");
              return;
            }
            // 🟢 Commander → preview pour CE produit précis (choix humain explicite).
            ask && ask(
              `Commande : ${action?.params?.product_name || block?.name} chez ${action?.params?.merchant_name || block?.merchant_name} — ${action?.params?.price_fcfa || block?.price_fcfa} FCFA`,
              { selectedProductId: action?.params?.product_id || block?.id || null, selectedMerchantId: action?.params?.merchant_id || block?.merchant_id || null }
            );
          }}
        />
      )}
      {/* Phase 3 — Carte de suivi VIVANTE (Resource Dispatch, contrat order_tracking A3). */}
      {isAgent && msg.agent_meta?.tracking_order_id && (
        <OrderTrackingLive orderId={msg.agent_meta.tracking_order_id} userId={userId} />
      )}
      {/* 2026-07-08 — Carte de critères (recherches complexes : immobilier, véhicule). */}
      {isAgent && msg.criteria_form && (
        <CriteriaFormCard domain={msg.criteria_form} onSubmit={(text) => ask && ask(text)} />
      )}
      {/* v3.17.1 — Panier Famille : lien de paiement diaspora généré dans le chat. */}
      {isAgent && msg.family_basket && (
        <FamilyBasketShareCard data={msg.family_basket} />
      )}
      {/* v3.18.0 — Espace Diaspora : liste des demandes du moteur unique. */}
      {isAgent && msg.diaspora_requests && (
        <DiasporaRequestsCard items={msg.diaspora_requests.items} />
      )}
      {/* v3.20.0 — GPS conversationnel : destination pré-remplie à confirmer. */}
      {isAgent && msg.gps_nav && (
        <GpsNavCard query={msg.gps_nav.query} />
      )}
      {/* v2.19.4a — Bouton "Réessayer" pour bulles blocked retryable (ex: LLM upstream HS). */}
      {role === "blocked" && msg.retryable && msg.lastUserMessage && ask && (
        <button
          type="button"
          data-testid={`retry-btn-${index}`}
          onClick={() => ask(msg.lastUserMessage)}
          style={{
            marginTop: 10,
            padding: "8px 14px",
            borderRadius: 999,
            border: "1.5px solid #FF7E1B",
            background: "#FF7E1B",
            color: "#FFFFFF",
            fontSize: 13.5,
            fontWeight: 700,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >🔄 Réessayer</button>
      )}
      {isAgent && tr && tr.original_fr && !tr.error && (
        <button
          type="button"
          className="civ-translation-toggle"
          onClick={() => setShowOriginal((v) => !v)}
          data-testid="translation-toggle-original"
        >
          {showOriginal ? `🌍 Voir la traduction (${LANG_LABEL_SHORT[tr.target_lang] || tr.target_lang})` : "🇫🇷 Voir l'original"}
        </button>
      )}
      {isAgent && msg.sponsored && (
        <SponsoredBlock sponsored={msg.sponsored} apiBase={apiBase} userId={userId} conversationId={conversationId} />
      )}
      {isAgent && !msg.streaming && Array.isArray(msg.sponsored_cards) && msg.sponsored_cards.length > 0 && (
        {/* v3.13.0 — SponsoredCards (seed factice marron) supprimées : la source
            de vérité est le système (annuaires + boutiques pro AYA). */}
      )}
      {isAgent && !msg.streaming && msg.adresse_card && (
        <AdresseCard data={msg.adresse_card} />
      )}
      {isAgent && !msg.streaming && Array.isArray(msg.rag_media) && msg.rag_media.length > 0 && (
        <>
          {/* 2026-06 — AYA PRIVATE ACTION ENGINE V1 : cartes d'action personnelles. */}
          {msg.rag_media.filter((m) => m && m.type === "action_preview" && m.data).map((m, i) => (
            <ActionPreviewCard key={"pa-prev-" + i} data={m.data} />
          ))}
          {msg.rag_media.filter((m) => m && m.type === "action_list" && m.data).map((m, i) => (
            <ActionListCard key={"pa-list-" + i} data={m.data} />
          ))}
          {msg.rag_media.filter((m) => m && m.type === "action_prepared" && m.data).map((m, i) => (
            <ActionPreparedCard key={"pa-prep-" + i} data={m.data} />
          ))}
          {msg.rag_media.filter((m) => m && m.type === "action_clarify" && m.data).map((m, i) => (
            <ActionClarifyCard key={"pa-cla-" + i} data={m.data} ask={ask} />
          ))}
          {msg.rag_media.filter((m) => m && m.type === "action_cart_add" && m.data).map((m, i) => (
            <ActionCartAddCard key={"pa-cart-" + i} data={m.data} />
          ))}
          {msg.rag_media.filter((m) => m && m.type === "action_denied" && m.data).map((m, i) => (
            <ActionDeniedCard key={"pa-den-" + i} data={m.data} />
          ))}
          {msg.rag_media.filter((m) => m && m.type === "address_navigate_card" && m.data).map((m, i) => (
            <AddressNavigateCard key={(m.data?.code || "x") + "-" + i} data={m.data} />
          ))}
          {msg.rag_media.filter((m) => m && m.type === "commerce_card" && m.data).map((m, i) => (
            <CommerceCard key={m.data?.properties?.id_etablissement || m.data?.properties?.id || i} feature={m.data} />
          ))}
          {msg.rag_media.filter((m) => m && m.type === "justice_grid" && m.data).map((m, i) => (
            <HorizontalCardList key={"jg-" + i} testId="justice-grid">
              {(m.data.profiles || []).map((p) => (
                <JusticeCard key={p.id} profile={p} />
              ))}
            </HorizontalCardList>
          ))}
          {/* v2.10.0 — health_grid renderer (symétrique à justice_grid). */}
          {msg.rag_media.filter((m) => m && m.type === "health_grid" && m.data).map((m, i) => (
            <HorizontalCardList key={"hg-" + i} testId="health-grid">
              {(m.data.profiles || []).map((p) => (
                <HealthCard key={p.id} profile={p} />
              ))}
            </HorizontalCardList>
          ))}
          {/* v2.16.0 — div_grid renderer : pattern strictement identique à FeaturedCards
              du mode Quotidien (classe civ-featured-row, padding 8/16/18, gap 12,
              scroll-snap x mandatory). Cards: 240px width, 110px image height. */}
          {msg.rag_media.filter((m) => m && m.type === "div_grid" && m.data).map((m, i) => (
            <HorizontalCardList key={"dg-" + i} testId="div-grid">
              {(m.data.items || []).map((it) => (
                <DivertissementCard
                  key={it.id}
                  item={it}
                  onAskMore={(item) => pushLocalDetail && pushLocalDetail(item)}
                />
              ))}
            </HorizontalCardList>
          ))}
          {/* v2.16.2 — emploi_grid renderer : style identique div_grid (240px cards). */}
          {msg.rag_media.filter((m) => m && m.type === "emploi_grid" && m.data).map((m, i) => (
            <HorizontalCardList key={"eg-" + i} testId="emploi-grid">
              {(m.data.items || []).map((it) => (
                <EmploiCard
                  key={it.id}
                  item={it}
                  onDetail={(item) => window.open(`/jobs/${item.id}`, "_blank")}
                />
              ))}
            </HorizontalCardList>
          ))}
          {/* v2.17.0 — admin_grid / finance_grid renderers. */}
          {msg.rag_media.filter((m) => m && m.type === "admin_grid" && m.data).map((m, i) => (
            <HorizontalCardList key={"ag-" + i} testId="admin-grid">
              {(m.data.items || []).map((it) => (
                <GenericDirectoryCard
                  key={it.id}
                  item={it}
                  accent={m.data.category?.accent || "#3B82F6"}
                  testIdPrefix="admin-card"
                  detailRoute="/admin"
                  titleField="service_name"
                  subtitleField="organism"
                  priceField="cost_display"
                />
              ))}
            </HorizontalCardList>
          ))}
          {msg.rag_media.filter((m) => m && m.type === "finance_grid" && m.data).map((m, i) => (
            <HorizontalCardList key={"fg-" + i} testId="finance-grid">
              {(m.data.items || []).map((it) => (
                <GenericDirectoryCard
                  key={it.id}
                  item={it}
                  accent={m.data.category?.accent || "#059669"}
                  testIdPrefix="finance-card"
                  detailRoute="/finance"
                  titleField="name"
                  subtitleField="type"
                  priceField="rate_display"
                />
              ))}
            </HorizontalCardList>
          ))}
          {/* v2.19.13 — shopping_grid renderer : 20 sous-catégories Shopping. */}
          {msg.rag_media.filter((m) => m && m.type === "shopping_grid" && m.data).map((m, i) => (
            <HorizontalCardList key={"sg-" + i} testId="shopping-grid">
              {(m.data.items || []).map((it) => (
                <GenericDirectoryCard
                  key={it.id}
                  item={it}
                  accent="#EC4899"
                  testIdPrefix="shopping-card"
                  detailRoute="/shopping"
                  titleField="name"
                  subtitleField="tagline"
                  priceField="price_range"
                />
              ))}
            </HorizontalCardList>
          ))}
          {/* v2.19.2c — featured_items_grid renderer : utilisé par les clicks
              de cards Quotidien (live/news/mode/beauté/etc.). Rend FeaturedCards inline. */}
          {msg.rag_media.filter((m) => m && m.type === "featured_items_grid" && m.data).map((m, i) => (
            <FeaturedCards
              key={"fig-" + i}
              items={m.data.items || []}
              categoryImage={m.data.categoryImage || {}}
              emptyHint={m.data.isLive ? "Aucun event live pour le moment." : `Aucun contenu featured pour ${m.data.category || "cette catégorie"}.`}
              testIdPrefix={m.data.isLive ? "live" : `featured-${m.data.category || "cat"}`}
              showPrice={!!m.data.isLive}
              onLearnMore={(it) => pushLocalDetail && pushLocalDetail(it)}
            />
          ))}
          {/* v3.12.0 — Boutiques & services AYA (minisites pros + mock). */}
          {msg.rag_media.filter((m) => m && m.type === "aya_shops_grid" && m.data).map((m, i) => (
            <HorizontalCardList key={"ayashop-" + i} testId="aya-shops-grid">
              {(m.data.items || []).map((it) => (
                <GenericDirectoryCard
                  key={it.id}
                  item={it}
                  accent="#10B981"
                  testIdPrefix="aya-shop-card"
                  detailRoute="/commercant"
                  titleField="name"
                  subtitleField="tagline"
                  favItemType="shop"
                />
              ))}
            </HorizontalCardList>
          ))}
          {/* v3.16.0 — Création d'adresse guidée depuis le chat. */}
          {msg.rag_media.filter((m) => m && m.type === "address_create_card").map((m, i) => (
            <AddressCreateCard key={m.data?.prompt_id || "ac-" + i} data={m.data || {}} />
          ))}
          {/* v3.11.0 — Kiosque ouvert par le LLM ("infos du jour"). */}
          {msg.rag_media.some((m) => m && m.type === "kiosque_strip") && (
            <KiosqueStrip />
          )}
          {/* v3.11.0 — Carte fin de résultats : "plus de détails ?" → lead form. */}
          {msg.rag_media.filter((m) => m && m.type === "detail_prompt" && m.data).map((m, i) => (
            <DetailPromptCard key={m.data?.prompt_id || "dp-" + i} data={m.data} />
          ))}
          {msg.rag_media.some((m) => m && m.type !== "commerce_card" && m.type !== "address_navigate_card" && m.type !== "justice_grid" && m.type !== "health_grid" && m.type !== "div_grid" && m.type !== "emploi_grid" && m.type !== "admin_grid" && m.type !== "finance_grid" && m.type !== "featured_items_grid" && m.type !== "shopping_grid" && m.type !== "detail_prompt" && m.type !== "kiosque_strip" && m.type !== "aya_shops_grid" && m.type !== "address_create_card") && (
            <RagMediaBlock media={msg.rag_media.filter((m) => m && m.type !== "commerce_card" && m.type !== "address_navigate_card" && m.type !== "justice_grid" && m.type !== "health_grid" && m.type !== "div_grid" && m.type !== "emploi_grid" && m.type !== "admin_grid" && m.type !== "finance_grid" && m.type !== "featured_items_grid" && m.type !== "shopping_grid" && m.type !== "detail_prompt" && m.type !== "kiosque_strip" && m.type !== "aya_shops_grid" && m.type !== "address_create_card")} />
          )}
        </>
      )}
      {/* v2.14.0 — Function calling : ui_components (map Mapbox + transport buttons + place_list). */}
      {isAgent && !msg.streaming && Array.isArray(msg.ui_components) && msg.ui_components.length > 0 && (
        <AgentToolBlock components={msg.ui_components} />
      )}
      {isAgent && !msg.streaming && msg.webSearch && <WebSearchSources webSearch={msg.webSearch} />}
      {msg.interrupted && !msg.isGreeting && msg.kind !== "system_quick_search" && (
        <div className="civ-bubble-meta civ-bubble-interrupted">⏹️ (interrompu)</div>
      )}
      {msg.voiceWarning && (
        <div className="civ-voice-warning">⚠️ {msg.voiceWarning}</div>
      )}
      {isAgent && onAction && <MessageActions message={msg} index={index} onAction={onAction} mode={mode} userId={userId} />}
    </div>
  );
}

const LANG_LABEL_SHORT = {
  bam_Latn: "Dioula 🇨🇮",
  fra_Latn: "Français 🇫🇷",
};

function SponsoredBlock({ sponsored, apiBase, userId, conversationId }) {
  const handleClick = async (e) => {
    if (!sponsored?.id) return;
    try {
      await fetch(`${apiBase}/sponsored/${sponsored.id}/click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, conversationId }),
      });
    } catch (_) {}
    // Let the default <a> open the URL in a new tab.
  };
  return (
    <div className="civ-sponsored" data-testid="sponsored-block">
      <div className="civ-sponsored-header">
        {sponsored.logo_url && (
          <img src={sponsored.logo_url} alt={sponsored.partner_name} className="civ-sponsored-logo" />
        )}
        <div className="civ-sponsored-meta">
          <span className="civ-sponsored-partner">{sponsored.partner_name}</span>
          <span className="civ-sponsored-tag">· {sponsored.disclosure || "Sponsorisé"}</span>
        </div>
      </div>
      <div className="civ-sponsored-headline">{sponsored.headline}</div>
      {sponsored.snippet && <div className="civ-sponsored-snippet">{sponsored.snippet}</div>}
      <a
        href={sponsored.url}
        target="_blank"
        rel="noopener noreferrer sponsored"
        className="civ-sponsored-cta"
        onClick={handleClick}
        data-testid="sponsored-cta"
      >
        {sponsored.cta_label || "En savoir plus"} →
      </a>
    </div>
  );
}

const CATEGORY_HEADERS = {
  news: "📰 Choisir un journal",
  mode: "👕 Choisir un partenaire mode",
  "beauté": "✨ Choisir un partenaire beauté",
  "marché": "🛒 Choisir une marketplace",
  tech: "💻 Choisir un média tech",
  sport: "⚽ Choisir un média sport",
  culture: "🎭 Choisir un média culture",
  mobile_money: "📱 Choisir un opérateur Mobile Money",
  business: "💼 Choisir un média business",
  abidjan_vibes: "🌴 Choisir un média lifestyle Abidjan",
};
const CATEGORY_READ_VERB = {
  news: "titres",
  mode: "tendances mode",
  "beauté": "tendances beauté",
  "marché": "produits & promotions",
  tech: "actus tech",
  sport: "résultats sportifs",
  culture: "événements culturels",
  mobile_money: "infos & tarifs",
  business: "actus business",
  abidjan_vibes: "bons plans lifestyle",
};
function categoryReadVerb(c) { return CATEGORY_READ_VERB[c] || "actualités"; }
function categoryHeader(c) { return CATEGORY_HEADERS[c] || `Choisir un partenaire ${prettyLabel(c)}`; }

// Structured Premium prompt builder — generates the editorial template defined in v1.8.0.
function buildPremiumPrompt(partner) {
  const now = new Date();
  const dateStr = now.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const timeStr = now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: "Africa/Abidjan" });
  return `Tu vas produire un résumé Premium structuré de ${partner.name} pour le ${dateStr} à ${timeStr} (heure d'Abidjan).

FORMAT OBLIGATOIRE (utilise les emojis comme séparateurs de sections) :

📅 Date : ${dateStr}
🕐 Heure : ${timeStr} (Abidjan)
📰 Source : ${partner.name}
🌐 ${partner.domain}

═══════════════════════════════════

## 🏛️ POLITIQUE & GOUVERNANCE
[Les 2-3 actualités politiques majeures du jour avec citations [1] [2]]

## 💼 ÉCONOMIE & BUSINESS
[Les 2-3 actualités éco/business avec citations]

## ⚽ SPORT
[Les 2-3 actualités sport avec citations]

## 🎭 SOCIÉTÉ & CULTURE
[Les 2-3 actualités société/culture avec citations]

## 🌍 INTERNATIONAL
[Si présent dans la source, 1-2 actualités internationales]

═══════════════════════════════════

## 📌 EN BREF
[3-5 bullet points concis des autres titres importants non couverts ci-dessus]

## 📚 SOURCES
[Liste des URLs cliquables des articles utilisés]

INSTRUCTIONS :
- Si une section n'a pas d'article disponible aujourd'hui, écris "Aucune actualité majeure aujourd'hui" sous la section (ne saute PAS la section).
- Utilise les sources Tavily fournies dans le bloc [CONTEXTE WEB TEMPS RÉEL].
- Cite TOUJOURS [1], [2], etc. après chaque info.
- Reste fidèle au ton du mode actif.
- Longueur cible : 600-1000 mots.

Source à scoper : ${partner.domain}`;
}

// Per-type button labels for the partner carousel cards.
// `newspaper` keeps the legacy "Lire / Premium 100 FCFA" pattern. All other types are
// FREE bio (left) + open URL or Google Maps (right).
function getPartnerActions(partner) {
  const hasAddr = !!(partner.physical_address && partner.physical_address.trim());
  const labelsByType = {
    newspaper:    { left: "📖 Lire",            right: "💰 Premium · 100 FCFA" },
    magazine:     { left: "📖 Articles",        right: "🌐 Site web" },
    brand:        { left: "📖 Histoire",        right: hasAddr ? "📍 Voir la boutique" : "🛍️ Boutique" },
    marketplace:  { left: "📖 À propos",        right: "🛒 Visiter" },
    operator:     { left: "📖 Découvrir",       right: "🌐 Site officiel" },
    blog:         { left: "📖 Articles récents", right: "🌐 Site" },
    organization: { left: "📖 À propos",        right: hasAddr ? "📍 Adresse" : "🌐 Site" },
    festival:     { left: "📖 À propos",        right: "🌐 Site officiel" },
  };
  return labelsByType[partner.type] || labelsByType.organization;
}

function PartnersCarousel({ category, partners, onClose, onLeft, onRight }) {
  if (!partners || partners.length === 0) return null;
  return (
    <div className="civ-newspapers" data-testid="partners-carousel" data-category={category}>
      <div className="civ-newspapers-head">
        <span>{categoryHeader(category)} :</span>
        <button
          type="button"
          className="civ-newspapers-close"
          onClick={onClose}
          data-testid="partners-close"
          aria-label="Fermer"
        >✕</button>
      </div>
      <div className="civ-newspapers-scroll">
        {partners.map((np) => {
          const labels = getPartnerActions(np);
          const isNewspaperPay = np.type === "newspaper";
          return (
            <div key={np.id} className="civ-newspaper-card" data-testid={`partner-card-${np.id}`} data-type={np.type} title={np.description || np.name}>
              <div className="civ-newspaper-head">
                {np.logo_url && <img src={np.logo_url} alt={np.name} className="civ-newspaper-favicon" loading="lazy" />}
                <span className="civ-newspaper-name" title={np.name}>{np.name}</span>
              </div>
              <div className="civ-newspaper-actions">
                {np.free_enabled !== false && (
                  <button
                    type="button"
                    className="civ-newspaper-btn civ-newspaper-btn-read"
                    onClick={() => onLeft(np)}
                    data-testid={`partner-read-${np.id}`}
                  >{labels.left}</button>
                )}
                {(isNewspaperPay ? np.premium_enabled !== false : true) && (
                  <button
                    type="button"
                    className={`civ-newspaper-btn ${isNewspaperPay ? "civ-newspaper-btn-pay" : "civ-newspaper-btn-link"}`}
                    onClick={() => onRight(np)}
                    data-testid={`partner-pay-${np.id}`}
                  >{labels.right}</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PaymentModal({ newspaper, userId, conversationId, apiBase, onClose, onSuccess }) {
  const [stage, setStage] = useState("choose"); // choose | processing | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [method, setMethod] = useState(null);
  const METHODS = [
    { id: "orange_money", label: "Orange Money", icon: "🟧" },
    { id: "mtn", label: "MTN MoMo", icon: "🟨" },
    { id: "wave", label: "Wave", icon: "🌊" },
    { id: "card", label: "Carte bancaire", icon: "💳" },
  ];
  const pay = async (m) => {
    setMethod(m);
    setStage("processing");
    try {
      const init = await fetch(`${apiBase}/payment/init`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId, conversationId,
          amount_fcfa: 100,
          purpose: "newspaper_summary",
          metadata: { newspaper: newspaper.id, newspaper_name: newspaper.name },
        }),
      }).then((r) => r.json());
      if (init.status !== "OK") throw new Error(init.error || "init_failed");
      const conf = await fetch(`${apiBase}/payment/${init.tx_id}/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ payment_method: m.id }),
      }).then((r) => r.json());
      if (conf.status !== "OK") throw new Error(conf.error || "confirm_failed");
      setStage("success");
      // brief success display before triggering the LLM action
      setTimeout(() => onSuccess(newspaper, conf.transaction), 700);
    } catch (e) {
      setStage("error");
      setErrorMsg(e.message || "Paiement échoué");
    }
  };
  return (
    <div className="civ-pay-backdrop" onClick={stage === "processing" ? null : onClose} data-testid="payment-modal">
      <div className="civ-pay-modal" onClick={(e) => e.stopPropagation()}>
        <div className="civ-pay-flag">🇨🇮</div>
        <h2>Résumé Premium · 100 FCFA</h2>
        <p className="civ-pay-sub">
          Paie 100 FCFA pour obtenir le résumé COMPLET de <strong>{newspaper.name}</strong> généré par AYA.
        </p>
        <div className="civ-pay-mock-note">⚠️ MOCK : aucun débit réel. Pour test uniquement.</div>

        {stage === "choose" && (
          <div className="civ-pay-methods">
            {METHODS.map((m) => (
              <button
                key={m.id}
                type="button"
                className="civ-pay-method"
                onClick={() => pay(m)}
                data-testid={`pay-method-${m.id}`}
              >
                <span className="civ-pay-method-icon">{m.icon}</span>
                <span className="civ-pay-method-label">{m.label}</span>
              </button>
            ))}
          </div>
        )}

        {stage === "processing" && (
          <div className="civ-pay-processing" data-testid="pay-processing">
            <div className="civ-pay-spinner" />
            <div>Traitement {method?.label}…</div>
            <small>Approuve sur ton téléphone (mock).</small>
          </div>
        )}

        {stage === "success" && (
          <div className="civ-pay-success" data-testid="pay-success">
            ✅ Paiement validé<br />
            <small>Génération du résumé premium…</small>
          </div>
        )}

        {stage === "error" && (
          <div className="civ-pay-error" data-testid="pay-error">
            ❌ {errorMsg}
            <div style={{ marginTop: 8 }}>
              <button type="button" className="civ-pay-cancel" onClick={() => setStage("choose")}>↻ Réessayer</button>
            </div>
          </div>
        )}

        {stage !== "processing" && (
          <button type="button" className="civ-pay-cancel" onClick={onClose} data-testid="pay-close">
            Annuler
          </button>
        )}
      </div>
    </div>
  );
}
